import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReusableModal from "../../components/common/ReusableModal";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CalendarX,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
  PencilLine,
  Tag,
  X,
} from "lucide-react";
import esewaLogo from "./esewa-logo.svg";
import razorpayLogo from "./razorpay-logo.svg";
import {
  clearEsewaRecoveryContext,
  clearEsewaTransactionSubmitted,
  getEsewaRecoveryContext,
  markEsewaTransactionSubmitted,
  saveEsewaRecoveryContext,
  wasEsewaTransactionSubmitted,
} from "./esewaSubmissionGuard";
import {
  buildRazorpayResultSearch,
  clearRazorpayRecoveryContext,
  getRazorpayRecoveryContext,
  getRazorpayRecoveryResumeAction,
  saveRazorpayRecoveryContext,
} from "./razorpayRecovery";
import {
  abandonPublicEsewaPayment,
  abandonPublicRazorpayPayment,
  createEsewaWebV2PaymentOrder,
  createPublicBookingHold,
  createReservationPaymentOrder,
  getPublicEsewaPaymentStatus,
  getPublicGroundSlots,
  previewPublicBookingPromo,
  releasePublicBookingHold,
  verifyReservationRazorpayPayment,
} from "./publicVenueApi";
import { resolveWebsiteCopy } from "./publicWebsiteContent";
import {
  savePublicPaymentConfirmationContext,
} from "./publicPaymentConfirmationContext";
import { PublicContactPhoneField } from "./PublicContactEnquiryStatus";
import {
  getPublicBookingSlotPriceLabel,
  getPublicBookingSummary,
  getPublicWebsiteSlotAmount,
} from "./publicBookingPricing";
import {
  buildPublicBookingContactNumber,
  isValidPublicBookingContactNumber,
  isValidPublicBookingNationalNumber,
  resolvePublicBookingPhoneCountryCode,
} from "./publicBookingPhone";
import { isPublicBookingCalendarDateSelected } from "./publicBookingCalendarSelection";

const PUBLIC_BOOKING_ERROR_TOAST_ID = "public-booking-error";


const RAZORPAY_CHECKOUT_SRC =
  "https://checkout.razorpay.com/v1/checkout.js";
let razorpayCheckoutLoaderPromise = null;

const loadRazorpayCheckout = () => {
  if (typeof window === "undefined") {
    return Promise.reject(
      new Error("Razorpay Checkout requires a browser."),
    );
  }

  if (window.Razorpay) {
    return Promise.resolve(window.Razorpay);
  }

  if (razorpayCheckoutLoaderPromise) {
    return razorpayCheckoutLoaderPromise;
  }

  razorpayCheckoutLoaderPromise = new Promise(
    (resolve, reject) => {
      const existing = document.querySelector(
        `script[src="${RAZORPAY_CHECKOUT_SRC}"]`,
      );
      const script =
        existing || document.createElement("script");
      let timeoutId = null;

      const cleanup = () => {
        script.removeEventListener("load", onLoad);
        script.removeEventListener("error", onError);
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
      };
      const onLoad = () => {
        cleanup();
        if (window.Razorpay) {
          resolve(window.Razorpay);
          return;
        }
        razorpayCheckoutLoaderPromise = null;
        reject(
          new Error("Razorpay Checkout did not initialise."),
        );
      };
      const onError = () => {
        cleanup();
        razorpayCheckoutLoaderPromise = null;
        reject(
          new Error("Unable to load Razorpay Checkout."),
        );
      };

      script.addEventListener("load", onLoad);
      script.addEventListener("error", onError);
      timeoutId = window.setTimeout(onError, 12000);

      if (!existing) {
        script.src = RAZORPAY_CHECKOUT_SRC;
        script.async = true;
        document.body.appendChild(script);
      }
    },
  );

  return razorpayCheckoutLoaderPromise;
};


const todayYmd = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getSlotStart = (slot) => slot?.start || slot?.startTime || "";
const getSlotEnd = (slot) => slot?.end || slot?.endTime || "";

const formatSlotTime = (value) => {
  const input = String(value || "").trim();

  const match = input.match(
    /^(\d{1,2}):(\d{2})(?::\d{2})?$/,
  );

  if (!match) {
    return input;
  }

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return input;
  }

  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${String(
    minute,
  ).padStart(2, "0")} ${period}`;
};

const formatCompactSlotTime = (value) => {
  const formatted = formatSlotTime(value);

  return formatted.replace(
    /:00\s(AM|PM)$/i,
    " $1",
  );
};

const getSlotLabel = (slot, fallback = "Slot") => {
  const start = getSlotStart(slot);
  const end = getSlotEnd(slot);
  return start && end
    ? `${formatSlotTime(start)} - ${formatSlotTime(end)}`
    : fallback;
};

const getCompactSlotLabel = (
  slot,
  fallback = "Slot",
) => {
  const start = getSlotStart(slot);
  const end = getSlotEnd(slot);

  if (!start || !end) return fallback;

  const startLabel =
    formatCompactSlotTime(start);
  const endLabel =
    formatCompactSlotTime(end);

  const startMatch = startLabel.match(
    /^(.+)\s(AM|PM)$/i,
  );
  const endMatch = endLabel.match(
    /^(.+)\s(AM|PM)$/i,
  );

  if (!startMatch || !endMatch) {
    return `${startLabel}–${endLabel}`;
  }

  const [, startTime, startPeriod] =
    startMatch;
  const [, endTime, endPeriod] =
    endMatch;

  return startPeriod.toUpperCase() ===
    endPeriod.toUpperCase()
    ? `${startTime}–${endTime} ${endPeriod.toUpperCase()}`
    : `${startTime} ${startPeriod.toUpperCase()}–${endTime} ${endPeriod.toUpperCase()}`;
};

const getSlotPriceLabel = (slot, fallback = "Price on request") => {
  const amount = getPublicWebsiteSlotAmount(
    slot,
    null,
  );

  const currency = slot?.currency || "INR";

  if (amount === null || amount === undefined || amount === "")
    return fallback;

  return `${currency} ${amount}`;
};

const isSlotAvailable = (slot) => slot?.available !== false;
const isSlotConflictError = (error) =>
  error?.response?.status === 409 &&
  error?.response?.data?.message ===
  "Slot no longer available";

const normalizePhone = (value) => String(value || "").replace(/[^\d]/g, "");

const parseYmdLocal = (value) => {
  const [year, month, day] = String(value || "")
    .split("-")
    .map(Number);

  if (!year || !month || !day) return null;

  const parsed = new Date(year, month - 1, day);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatYmdLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const addOneCalendarMonthClamped = (
  date,
) => {
  const targetYear = date.getFullYear();
  const targetMonth = date.getMonth() + 1;

  const lastDayOfTargetMonth =
    new Date(
      targetYear,
      targetMonth + 1,
      0,
    ).getDate();

  return new Date(
    targetYear,
    targetMonth,
    Math.min(
      date.getDate(),
      lastDayOfTargetMonth,
    ),
  );
};

const formatBookingDate = (value, fallback = "Select a date") => {
  const parsed = parseYmdLocal(value);

  if (!parsed) return value || fallback;

  return new Intl.DateTimeFormat("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsed);
};

const getHoldSecondsRemaining = (expiresAt) => {
  const expiryTime = new Date(expiresAt).getTime();

  if (!Number.isFinite(expiryTime)) {
    return null;
  }

  return Math.max(
    0,
    Math.ceil((expiryTime - Date.now()) / 1000),
  );
};

const isHoldExpired = (expiresAt) =>
  getHoldSecondsRemaining(expiresAt) === 0;

const formatHoldCountdown = (seconds) => {
  const safeSeconds = Math.max(
    0,
    Number(seconds) || 0,
  );

  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(
    remainingSeconds,
  ).padStart(2, "0")}`;
};

const clearEsewaRecoverySession = (storage, reservationId = "") => {
  const recovery = getEsewaRecoveryContext(storage);
  const normalizedReservationId = String(reservationId || "").trim();

  if (
    normalizedReservationId &&
    recovery?.reservationId &&
    String(recovery.reservationId) !== normalizedReservationId
  ) {
    return false;
  }

  if (recovery?.transactionUuid) {
    clearEsewaTransactionSubmitted(
      storage,
      recovery.transactionUuid,
    );
  }

  clearEsewaRecoveryContext(storage);
  return true;
};

const buildCalendarDays = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(year, month, index - firstWeekday + 1);

    return {
      date,
      ymd: formatYmdLocal(date),
      dayNumber: date.getDate(),
      isCurrentMonth: date.getMonth() === month,
    };
  });
};

const PublicBookingModal = ({
  open,
  slug,
  venue,
  grounds = [],
  initialGround,
  website,
  primaryColor = "#33A444",
  accentColor = "#111827",
  resumeEsewaRecovery = false,
  onEsewaRecoveryHandled,
  resumeRazorpayRecovery = false,
  onRazorpayRecoveryHandled,
  recoveryIntent = "retry",
  onClose,
  onHoldCreated,
}) => {
  const t = useCallback(
    (key, fallback) =>
      resolveWebsiteCopy(website, key, fallback),
    [website],
  );

  const calendarWeekdays = [
    t("weekdaySun", "Sun"),
    t("weekdayMon", "Mon"),
    t("weekdayTue", "Tue"),
    t("weekdayWed", "Wed"),
    t("weekdayThu", "Thu"),
    t("weekdayFri", "Fri"),
    t("weekdaySat", "Sat"),
  ];

  const bookingSteps = [
    {
      number: 1,
      title: t("stepOneShortTitle", "Slot"),
      description: t("stepOneShortDescription", "Choose date and time"),
    },
    {
      number: 2,
      title: t("stepTwoShortTitle", "Details"),
      description: t("stepTwoShortDescription", "Enter your information"),
    },
    {
      number: 3,
      title: t("stepThreeShortTitle", "Pay"),
      description: t("stepThreeShortDescription", "Complete payment"),
    },
  ];

  const slotSize = Number(website?.booking?.slotSize || 60);
  const timezone = venue?.policies?.timezone || "Asia/Kolkata";
  const requirePhone = website?.booking?.requirePhone !== false;
  const requireEmail = website?.booking?.requireEmail === true;

  const [selectedGroundId, setSelectedGroundId] = useState("");
  const [selectedDate, setSelectedDate] = useState(todayYmd());
  const [slots, setSlots] = useState([]);
  const allSlotsBooked =
    slots.length > 0 &&
    slots.every((slot) => !isSlotAvailable(slot));
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedSlotsOpen, setSelectedSlotsOpen] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [successHold, setSuccessHold] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [razorpayVerifying, setRazorpayVerifying] = useState(false);
  const [razorpayResult, setRazorpayResult] = useState(null);
  const [razorpayVerificationPayload, setRazorpayVerificationPayload] =
    useState(null);
  const [razorpayPaymentPending, setRazorpayPaymentPending] = useState(false);
  const [razorpayAbandonmentLoading, setRazorpayAbandonmentLoading] =
    useState(false);
  const [esewaRedirecting, setEsewaRedirecting] = useState(false);
  const [esewaRecoveryState, setEsewaRecoveryState] = useState("idle");
  const [esewaAbandonmentLoading, setEsewaAbandonmentLoading] =
    useState(false);

  const [holdSecondsRemaining, setHoldSecondsRemaining] =
    useState(null);

  const [slotRefreshKey, setSlotRefreshKey] =
    useState(0);
  const bookingContentRef = useRef(null);
  const esewaSubmittingRef = useRef(false);
  const paymentStartedRef = useRef(false);
  const paymentRequestInFlightRef = useRef(false);
  const releaseInFlightRef = useRef(false);
  const esewaAbandonmentRef = useRef(false);
  const reconciliationInFlightRef = useRef(false);
  const razorpayAbandonForRetryRef = useRef(null);

  const [step, setStep] = useState(1);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = parseYmdLocal(todayYmd()) || new Date();

    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  const [customer, setCustomer] = useState({
    userName: "",
    contactNumber: "",
    email: "",
  });
  const [phoneCountryCode, setPhoneCountryCode] = useState(() =>
    resolvePublicBookingPhoneCountryCode(venue),
  );
  const [promoCode, setPromoCode] = useState("");
  const [promoExpanded, setPromoExpanded] = useState(false);
  const [promoApplying, setPromoApplying] = useState(false);
  const [promoPreview, setPromoPreview] = useState(null);

  const setErrorMessage = useCallback((message) => {
    const text = String(message || "").trim();

    toast.dismiss(PUBLIC_BOOKING_ERROR_TOAST_ID);

    if (!text) return;

    toast.error(text, {
      toastId: PUBLIC_BOOKING_ERROR_TOAST_ID,
      position: "top-center",
      autoClose: 4200,
    });
  }, []);

  const resetBookingSessionState = useCallback(
    ({ preserveRecovery = false } = {}) => {
      if (!preserveRecovery) {
        clearEsewaRecoverySession(
          window.sessionStorage,
        );
        clearRazorpayRecoveryContext(
          window.sessionStorage,
        );
      }

      const freshDate = todayYmd();
      const parsedFreshDate =
        parseYmdLocal(freshDate) || new Date();
      const freshGround =
        initialGround || grounds[0] || null;

      toast.dismiss(PUBLIC_BOOKING_ERROR_TOAST_ID);

      setSelectedGroundId(freshGround?._id || "");
      setSelectedDate(freshDate);
      setCalendarMonth(
        new Date(
          parsedFreshDate.getFullYear(),
          parsedFreshDate.getMonth(),
          1,
        ),
      );
      setSlots([]);
      setSelectedItems([]);
      setSelectedSlotsOpen(false);
      setLoadingSlots(false);
      setSuccessHold(null);
      setPaymentLoading(false);
      setPaymentOrder(null);
      setRazorpayVerifying(false);
      setRazorpayResult(null);
      setRazorpayVerificationPayload(null);
      setRazorpayPaymentPending(false);
      setRazorpayAbandonmentLoading(false);
      setEsewaRedirecting(false);
      setEsewaRecoveryState("idle");
      setEsewaAbandonmentLoading(false);
      setHoldSecondsRemaining(null);
      setStep(1);
      setCustomer({
        userName: "",
        contactNumber: "",
        email: "",
      });
      setPhoneCountryCode(
        resolvePublicBookingPhoneCountryCode(venue),
      );
      setPromoCode("");
      setPromoExpanded(false);
      setPromoApplying(false);
      setPromoPreview(null);
      setSlotRefreshKey((value) => value + 1);

      esewaSubmittingRef.current = false;
      paymentStartedRef.current = false;
      paymentRequestInFlightRef.current = false;
    },
    [grounds, initialGround, venue],
  );

  const selectedGround = useMemo(
    () =>
      grounds.find((ground) => String(ground._id) === String(selectedGroundId)),
    [grounds, selectedGroundId],
  );

  const selectedItemsTotal = useMemo(
    () =>
      selectedItems.reduce(
        (sum, item) =>
          sum +
          getPublicWebsiteSlotAmount(item, 0),
        0,
      ),
    [selectedItems],
  );

  const selectedItemGroups = useMemo(() => {
    const groups = new Map();

    selectedItems.forEach((item) => {
      const dateKey = String(item?.date || "");
      const existing = groups.get(dateKey) || [];
      existing.push(item);
      groups.set(dateKey, existing);
    });

    return Array.from(groups.entries()).map(([date, items]) => ({
      date,
      items,
    }));
  }, [selectedItems]);

  const previewReservation = useMemo(
    () =>
      promoPreview
        ? {
            slot_price: promoPreview.subtotal,
            discount_amount: promoPreview.discountAmount,
            final_amount: promoPreview.finalAmount,
            dueAmount: promoPreview.finalAmount,
            promo_code: promoPreview.code,
          }
        : null,
    [promoPreview],
  );

  const bookingSummary = useMemo(
    () =>
      getPublicBookingSummary({
        selectedItemsTotal,
        reservation: successHold || previewReservation,
      }),
    [selectedItemsTotal, successHold, previewReservation],
  );

  const calendarDays = useMemo(
    () => buildCalendarDays(calendarMonth),
    [calendarMonth],
  );

  const calendarMonthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat("en-IN", {
        month: "long",
        year: "numeric",
      }).format(calendarMonth),
    [calendarMonth],
  );

  const bookingCurrency =
    selectedItems[0]?.currency || venue?.pricing?.currency || "INR";

  const selectedGroundName =
    selectedGround?.name ||
    selectedGround?.type ||
    t("selectedGroundFallback", "Selected ground");

  const venueName =
    String(venue?.name || "").trim() ||
    t("venueFallbackName", "Venue");

  const bookingSummaryImage =
    selectedGround?.mainImageUrl ||
    selectedGround?.imageUrl ||
    venue?.media?.bannerImage ||
    venue?.media?.gallery?.[0] ||
    "";

  const selectedDateLabel = formatBookingDate(
    selectedDate,
    t("selectDateFallback", "Select a date"),
  );

  const customerPhoneDisplay =
    buildPublicBookingContactNumber(
      normalizePhone(customer.contactNumber),
      phoneCountryCode,
    ) ||
    t("notProvidedLabel", "Not provided");

  const customerEmailDisplay =
    customer.email.trim() ||
    t("notProvidedLabel", "Not provided");

  const currentTodayYmd = todayYmd();

  const currentTodayDate =
    parseYmdLocal(currentTodayYmd) ||
    new Date();

  const maxBookingDate =
    addOneCalendarMonthClamped(
      currentTodayDate,
    );

  const maxBookingYmd =
    formatYmdLocal(maxBookingDate);

  const firstAllowedMonth =
    new Date(
      currentTodayDate.getFullYear(),
      currentTodayDate.getMonth(),
      1,
    );

  const lastAllowedMonth =
    new Date(
      maxBookingDate.getFullYear(),
      maxBookingDate.getMonth(),
      1,
    );

  const canViewPreviousMonth =
    calendarMonth.getTime() >
    firstAllowedMonth.getTime();

  const canViewNextMonth =
    calendarMonth.getTime() <
    lastAllowedMonth.getTime();

  useEffect(() => {
    if (!open) return;

    const esewaRecovery =
      getEsewaRecoveryContext(
        window.sessionStorage,
      );
    const razorpayRecovery =
      getRazorpayRecoveryContext(
        window.sessionStorage,
      );

    const matchingRecoveries = [
      esewaRecovery?.slug === slug
        ? {
            gateway: "ESEWA",
            context: esewaRecovery,
          }
        : null,
      razorpayRecovery?.slug === slug
        ? {
            gateway: "RAZORPAY",
            context: razorpayRecovery,
          }
        : null,
    ]
      .filter(Boolean)
      .sort(
        (left, right) =>
          Number(right.context?.startedAt || 0) -
          Number(left.context?.startedAt || 0),
      );

    const activeRecovery =
      matchingRecoveries[0] || null;
    const recovery = activeRecovery?.context || null;
    const isRecoveringEsewa =
      activeRecovery?.gateway === "ESEWA";
    const isRecoveringRazorpay =
      activeRecovery?.gateway === "RAZORPAY";
    const isRecoveringPayment = Boolean(
      recovery?.reservationId &&
        recovery?.releaseToken,
    );
    const recoverySnapshot =
      isRecoveringPayment
        ? recovery.bookingSnapshot
        : null;

    const snapshotGroundId = String(
      recoverySnapshot?.selectedGroundId || "",
    );

    const restoredGround =
      grounds.find(
        (ground) =>
          String(ground?._id || "") ===
          snapshotGroundId,
      ) ||
      initialGround ||
      grounds[0] ||
      null;

    const initialDate =
      String(
        recoverySnapshot?.selectedDate || "",
      ).trim() || todayYmd();

    const parsedInitialDate =
      parseYmdLocal(initialDate) ||
      new Date();

    setSelectedGroundId(
      restoredGround?._id || "",
    );
    setSelectedDate(initialDate);
    setCalendarMonth(
      new Date(
        parsedInitialDate.getFullYear(),
        parsedInitialDate.getMonth(),
        1,
      ),
    );
    setSlots([]);
    setSelectedItems(
      isRecoveringPayment
        ? recoverySnapshot?.selectedItems || []
        : [],
    );
    setSelectedSlotsOpen(false);
    setErrorMessage("");
    setSuccessHold(
      isRecoveringPayment
        ? {
            _id: recovery.reservationId,
            releaseToken:
              recovery.releaseToken,
            expiresAt:
              recovery.expiresAt ||
              null,
          }
        : null,
    );
    setPaymentOrder(
      isRecoveringRazorpay
        ? recovery.paymentOrder || null
        : null,
    );
    setPaymentLoading(false);
    setRazorpayVerifying(false);
    setRazorpayResult(null);
    setRazorpayVerificationPayload(
      isRecoveringRazorpay
        ? recovery.verificationPayload || null
        : null,
    );
    setRazorpayPaymentPending(
      Boolean(
        isRecoveringRazorpay &&
          (recovery.verificationPayload ||
            recovery.state === "pending"),
      ),
    );
    setEsewaRedirecting(false);
    setEsewaRecoveryState(
      isRecoveringEsewa
        ? "checking"
        : "idle",
    );
    esewaSubmittingRef.current = false;
    paymentStartedRef.current =
      isRecoveringPayment;
    paymentRequestInFlightRef.current = false;
    releaseInFlightRef.current = false;
    setHoldSecondsRemaining(
      isRecoveringPayment &&
        recovery.expiresAt
        ? getHoldSecondsRemaining(
            recovery.expiresAt,
          )
        : null,
    );
    setStep(isRecoveringPayment ? 3 : 1);
    setCustomer(
      isRecoveringPayment
        ? {
            userName:
              recoverySnapshot?.customer
                ?.userName || "",
            contactNumber:
              recoverySnapshot?.customer
                ?.contactNumber || "",
            email:
              recoverySnapshot?.customer
                ?.email || "",
          }
        : {
            userName: "",
            contactNumber: "",
            email: "",
          },
    );
    setPhoneCountryCode(
      isRecoveringPayment &&
        recoverySnapshot
          ?.phoneCountryCode
        ? recoverySnapshot.phoneCountryCode
        : resolvePublicBookingPhoneCountryCode(
            venue,
          ),
    );
    setPromoCode(
      isRecoveringPayment
        ? recoverySnapshot?.promoCode || ""
        : "",
    );
    setPromoExpanded(false);
    setPromoApplying(false);
    setPromoPreview(
      isRecoveringPayment
        ? recoverySnapshot?.promoPreview ||
            null
        : null,
    );
  }, [
    open,
    initialGround,
    grounds,
    venue,
    slug,
    setErrorMessage,
  ]);

  useEffect(() => {
    let active = true;

    const loadSlots = async () => {
      if (!open || !slug || !selectedGroundId || !selectedDate) return;

      try {
        setLoadingSlots(true);

        const data = await getPublicGroundSlots(
          slug,
          selectedGroundId,
          selectedDate,
          slotSize,
          timezone,
        );

        if (!active) return;

        setSlots(Array.isArray(data?.slots) ? data.slots : []);
      } catch (error) {
        if (!active) return;

        setSlots([]);
        setErrorMessage(
          error?.response?.data?.message ||
          error?.message ||
          t("slotsLoadErrorText", "Unable to load available slots."),
        );
      } finally {
        if (active) setLoadingSlots(false);
      }
    };

    loadSlots();

    return () => {
      active = false;
    };
  }, [
    open,
    slug,
    selectedGroundId,
    selectedDate,
    slotSize,
    timezone,
    slotRefreshKey,
    t,
  ]);

  useEffect(() => {
    if (!open || !successHold?.expiresAt) {
      return undefined;
    }

    const updateCountdown = () => {
      setHoldSecondsRemaining(
        getHoldSecondsRemaining(
          successHold.expiresAt,
        ),
      );
    };

    updateCountdown();

    const intervalId = window.setInterval(
      updateCountdown,
      1000,
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, [open, successHold?.expiresAt]);

  useEffect(() => {
    if (
      !successHold ||
      holdSecondsRemaining !== 0
    ) {
      return;
    }

    const esewaRecovery =
      getEsewaRecoveryContext(
        window.sessionStorage,
      );
    const razorpayRecovery =
      getRazorpayRecoveryContext(
        window.sessionStorage,
      );
    const reservationId = String(
      successHold?._id || "",
    );
    const esewaIsBeingReconciled = Boolean(
      esewaRecovery?.reservationId &&
        String(esewaRecovery.reservationId) ===
          reservationId,
    );
    const razorpayIsBeingVerified = Boolean(
      razorpayRecovery?.reservationId &&
        String(razorpayRecovery.reservationId) ===
          reservationId &&
        razorpayRecovery.verificationPayload,
    );

    if (esewaIsBeingReconciled) {
      setEsewaRecoveryState((current) =>
        current === "idle"
          ? "checking"
          : current,
      );
      return;
    }

    if (razorpayIsBeingVerified) {
      // A successful Razorpay Checkout callback may still be awaiting provider
      // capture/server confirmation. Never discard that signed payload merely
      // because the original slot-hold timer reached zero.
      return;
    }

    clearRazorpayRecoveryContext(
      window.sessionStorage,
    );
    setPaymentLoading(false);
    setPaymentOrder(null);
    setSuccessHold(null);
    setSelectedItems([]);
    setHoldSecondsRemaining(null);
    setStep(1);

    setSlotRefreshKey((value) => value + 1);

    setErrorMessage(
      t(
        "holdExpiredReleasedText",
        "Your payment time expired. The slot is available again. Please select a slot to continue.",
      ),
    );
  }, [successHold, holdSecondsRemaining, t]);

  const setCustomerField = (key, value) => {
    setPromoPreview(null);
    setCustomer((prev) => ({ ...prev, [key]: value }));
  };

  const getSelectedItemKey = (item) => `${item.date}|${item.start}|${item.end}`;

  const toggleSlotSelection = (slot) => {
    if (!isSlotAvailable(slot)) return;

    setErrorMessage("");
    setPromoPreview(null);

    const item = {
      date: selectedDate,
      start: getSlotStart(slot),
      end: getSlotEnd(slot),
      price: slot?.price,
      discountedPrice: slot?.discountedPrice,
      currency: slot?.currency || venue?.pricing?.currency || "INR",
    };

    const key = getSelectedItemKey(item);

    setSelectedItems((prev) => {
      const exists = prev.some((entry) => getSelectedItemKey(entry) === key);

      if (exists) {
        return prev.filter((entry) => getSelectedItemKey(entry) !== key);
      }

      return [...prev, item].sort((a, b) =>
        `${a.date} ${a.start}`.localeCompare(`${b.date} ${b.start}`),
      );
    });
  };

  const removeSelectedItem = (itemToRemove) => {
    const key = getSelectedItemKey(itemToRemove);
    const nextItems = selectedItems.filter(
      (item) => getSelectedItemKey(item) !== key,
    );

    setPromoPreview(null);
    setSelectedItems(nextItems);

    if (!nextItems.length) {
      setSelectedSlotsOpen(false);
    }

    if (!nextItems.length && step > 1 && !successHold) {
      setPromoExpanded(false);
      setErrorMessage("");
      setStep(1);

      toast.info(
        t(
          "allSlotsRemovedText",
          "Select a new slot to continue. Your contact details are saved.",
        ),
        {
          position: "top-center",
          autoClose: 3000,
        },
      );

      requestAnimationFrame(() => {
        bookingContentRef.current
          ?.closest(".rm-body")
          ?.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  };

  const validateSlotStep = () => {
    if (!selectedGroundId) {
      return t("validationGround", "Please select a ground.");
    }
    if (!selectedDate) {
      return t("validationDate", "Please select a date.");
    }

    if (!selectedItems.length) {
      return t(
        "validationSlot",
        "Please select at least one available slot.",
      );
    }

    return "";
  };

  const validateCustomerStep = () => {
    const userName = customer.userName.trim();
    const phone = normalizePhone(customer.contactNumber);
    const contactNumber = buildPublicBookingContactNumber(
      phone,
      phoneCountryCode,
    );
    const email = customer.email.trim();

    if (!userName) {
      return t("validationName", "Please enter customer name.");
    }

    if (requirePhone && !phone) {
      return t(
        "validationPhone",
        "Please enter customer phone number.",
      );
    }

    if (
      phone &&
      (!isValidPublicBookingNationalNumber(phone) ||
        !isValidPublicBookingContactNumber(contactNumber))
    ) {
      return t(
        "validationPhoneFormat",
        "Please enter a valid phone number for the selected country code.",
      );
    }

    if (requireEmail && !email) {
      return t(
        "validationEmail",
        "Please enter customer email.",
      );
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return t(
        "validationEmailFormat",
        "Please enter a valid email address.",
      );
    }

    return "";
  };

  const validate = () => validateSlotStep() || validateCustomerStep();

  const buildBookingPayload = useCallback((promoCodeValue = "") => {
    const normalizedPhone = normalizePhone(customer.contactNumber);
    const normalizedContactNumber =
      buildPublicBookingContactNumber(
        normalizedPhone,
        phoneCountryCode,
      );
    const normalizedPromo = String(promoCodeValue || "")
      .trim()
      .toUpperCase();

    return {
      userName: customer.userName.trim(),
      contactNumber: normalizedPhone || undefined,
      phoneCountryCode: normalizedContactNumber
        ? phoneCountryCode
        : undefined,
      email: customer.email.trim() || undefined,
      groundId: selectedGroundId,
      items: selectedItems.map((item) => ({
        date: item.date,
        start: item.start,
        end: item.end,
      })),
      promoCode: normalizedPromo || undefined,
      isUserReservation: true,
    };
  }, [
    customer.contactNumber,
    customer.email,
    customer.userName,
    phoneCountryCode,
    selectedGroundId,
    selectedItems,
  ]);

  const handleApplyPromo = async () => {
    if (promoApplying || paymentLoading || esewaRedirecting) return;

    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    const normalizedPromo = promoCode.trim().toUpperCase();
    if (!normalizedPromo) {
      setErrorMessage(
        t("promoCodeRequiredText", "Please enter a promo code."),
      );
      return;
    }

    try {
      setPromoApplying(true);
      setErrorMessage("");

      const data = await previewPublicBookingPromo(
        slug,
        buildBookingPayload(normalizedPromo),
        { slotSize },
      );

      const pricing = data?.pricing || {};
      const appliedCode = String(
        data?.promo?.code || normalizedPromo,
      )
        .trim()
        .toUpperCase();

      const nextPreview = {
        code: appliedCode,
        description: String(data?.promo?.description || "").trim(),
        subtotal: Number(pricing.subtotal || 0),
        discountAmount: Number(pricing.discountAmount || 0),
        finalAmount: Number(pricing.finalAmount || 0),
      };

      setPromoCode(appliedCode);
      setPromoPreview(nextPreview);

      toast.success(
        nextPreview.discountAmount > 0
          ? `${appliedCode} applied. You save ${bookingCurrency} ${nextPreview.discountAmount}.`
          : `${appliedCode} applied.`,
        { position: "top-center", autoClose: 3200 },
      );
    } catch (error) {
      setPromoPreview(null);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          t("promoApplyErrorText", "Promo code could not be applied."),
      );
    } finally {
      setPromoApplying(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoPreview(null);
    setPromoCode("");
    setPromoExpanded(true);
    setErrorMessage("");
  };

  const handleContinueToDetails = () => {
    const validationError = validateSlotStep();

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setErrorMessage("");
    setStep(2);

    requestAnimationFrame(() => {
      bookingContentRef.current
        ?.closest(".rm-body")
        ?.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const handleBackToSlots = () => {
    setErrorMessage("");
    setPromoPreview(null);
    setStep(1);

    requestAnimationFrame(() => {
      bookingContentRef.current
        ?.closest(".rm-body")
        ?.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const handleBackToDetails = () => {
    if (
      successHold ||
      paymentLoading ||
      esewaRedirecting ||
      esewaRecoveryState === "pending" ||
      esewaRecoveryState === "checking"
    ) {
      return;
    }

    setErrorMessage("");
    setStep(2);

    requestAnimationFrame(() => {
      bookingContentRef.current
        ?.closest(".rm-body")
        ?.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const refreshSelectionAfterEsewaRelease = useCallback(
    async () => {
      if (!selectedItems.length || !selectedGroundId) {
        setSlots([]);
        setSelectedItems([]);
        setPromoPreview(null);
        setStep(1);
        setSlotRefreshKey((value) => value + 1);

        return "no_selection";
      }

      const selectedDates = Array.from(
        new Set(
          selectedItems
            .map((item) =>
              String(item?.date || "").trim(),
            )
            .filter(Boolean),
        ),
      );

      if (!selectedDates.length) {
        setSlots([]);
        setSelectedItems([]);
        setPromoPreview(null);
        setStep(1);
        setSlotRefreshKey((value) => value + 1);

        return "no_selection";
      }

      try {
        const refreshedEntries = await Promise.all(
          selectedDates.map(async (date) => {
            const data = await getPublicGroundSlots(
              slug,
              selectedGroundId,
              date,
              slotSize,
              timezone,
            );

            return [
              date,
              Array.isArray(data?.slots)
                ? data.slots
                : [],
            ];
          }),
        );

        const slotsByDate = new Map(
          refreshedEntries,
        );
        const currentDateSlots =
          slotsByDate.get(
            String(selectedDate || "").trim(),
          );

        if (currentDateSlots) {
          setSlots(currentDateSlots);
        } else {
          setSlotRefreshKey(
            (value) => value + 1,
          );
        }

        const selectionStillAvailable =
          selectedItems.every((item) => {
            const itemDate = String(
              item?.date || "",
            ).trim();
            const itemStart = String(
              item?.start || "",
            ).trim();
            const itemEnd = String(
              item?.end || "",
            ).trim();

            return (
              slotsByDate.get(itemDate) || []
            ).some(
              (slot) =>
                isSlotAvailable(slot) &&
                String(
                  getSlotStart(slot) || "",
                ).trim() === itemStart &&
                String(
                  getSlotEnd(slot) || "",
                ).trim() === itemEnd,
            );
          });

        if (!selectionStillAvailable) {
          setSelectedItems([]);
          setPromoPreview(null);
          setStep(1);

          return "unavailable";
        }

        setStep(3);
        return "available";
      } catch (_error) {
        setSlots([]);
        setSelectedItems([]);
        setPromoPreview(null);
        setStep(1);
        setSlotRefreshKey(
          (value) => value + 1,
        );

        return "unknown";
      }
    },
    [
      selectedDate,
      selectedGroundId,
      selectedItems,
      slug,
      slotSize,
      timezone,
    ],
  );

  const abandonEsewaForRetry = useCallback(
    async ({ silent = false } = {}) => {
      if (
        !open ||
        !slug ||
        releaseInFlightRef.current
      ) {
        return "busy";
      }

      const recovery =
        getEsewaRecoveryContext(
          window.sessionStorage,
        );
      const recoveryMatchesSlug =
        recovery?.slug === slug;
      const reservationId = String(
        (recoveryMatchesSlug
          ? recovery?.reservationId
          : "") ||
          successHold?._id ||
          "",
      ).trim();
      const releaseToken = String(
        (recoveryMatchesSlug
          ? recovery?.releaseToken
          : "") ||
          successHold?.releaseToken ||
          "",
      ).trim();
      const transactionUuid = String(
        recoveryMatchesSlug
          ? recovery?.transactionUuid || ""
          : "",
      ).trim();

      if (!reservationId || !releaseToken) {
        return "missing";
      }

      releaseInFlightRef.current = true;
      esewaAbandonmentRef.current = true;
      esewaSubmittingRef.current = false;
      setEsewaRedirecting(false);
      setEsewaRecoveryState("checking");
      setEsewaAbandonmentLoading(true);

      try {
        const data =
          await abandonPublicEsewaPayment(
            slug,
            reservationId,
            releaseToken,
          );
        const state = String(
          data?.state || "",
        ).toLowerCase();
        const responseTransactionUuid =
          String(
            data?.transactionUuid ||
              transactionUuid ||
              "",
          ).trim();

        if (state === "completed") {
          if (responseTransactionUuid) {
            clearEsewaTransactionSubmitted(
              window.sessionStorage,
              responseTransactionUuid,
            );
          }
          clearEsewaRecoverySession(
            window.sessionStorage,
            reservationId,
          );
          setEsewaRecoveryState("completed");

          if (data?.redirectUrl) {
            window.location.replace(
              data.redirectUrl,
            );
          } else if (!silent) {
            toast.success(
              t(
                "esewaPaymentCompletedText",
                "Payment completed successfully. Your booking is confirmed.",
              ),
              {
                position: "top-center",
                autoClose: 4200,
              },
            );
          }

          return "completed";
        }

        if (state === "abandoned_paid") {
          if (responseTransactionUuid) {
            clearEsewaTransactionSubmitted(
              window.sessionStorage,
              responseTransactionUuid,
            );
          }
          clearEsewaRecoverySession(
            window.sessionStorage,
            reservationId,
          );
          paymentStartedRef.current = false;
          paymentRequestInFlightRef.current = false;
          setEsewaRecoveryState("idle");
          setPaymentOrder(null);
          setSuccessHold(null);
          setHoldSecondsRemaining(null);
          setSelectedSlotsOpen(false);
          setSelectedItems([]);
          setPromoPreview(null);
          setStep(1);
          setSlotRefreshKey(
            (value) => value + 1,
          );

          toast.error(
            t(
              "esewaAbandonedLatePaymentText",
              "Payment was received for a booking attempt that had already been released. Do not pay again. Please contact support with your eSewa transaction reference.",
            ),
            {
              position: "top-center",
              autoClose: 7000,
            },
          );

          return "abandoned_paid";
        }

        if (state === "processing") {
          paymentStartedRef.current = true;
          setEsewaRecoveryState("checking");

          if (!silent) {
            toast.info(
              t(
                "esewaAbandonProcessingText",
                "Your payment was already being confirmed when you returned. Please do not pay again while we finish checking it.",
              ),
              {
                position: "top-center",
                autoClose: 5000,
              },
            );
          }

          return "processing";
        }

        if (state !== "abandoned") {
          throw new Error(
            data?.message ||
              "The previous eSewa booking could not be released.",
          );
        }

        if (responseTransactionUuid) {
          clearEsewaTransactionSubmitted(
            window.sessionStorage,
            responseTransactionUuid,
          );
        }
        clearEsewaRecoverySession(
          window.sessionStorage,
          reservationId,
        );

        paymentStartedRef.current = false;
        paymentRequestInFlightRef.current = false;
        setEsewaRecoveryState("idle");
        setPaymentOrder(null);
        setSuccessHold(null);
        setHoldSecondsRemaining(null);
        setSelectedSlotsOpen(false);

        const selectionState =
          await refreshSelectionAfterEsewaRelease();

        if (!silent) {
          if (selectionState === "available") {
            toast.error(
              t(
                "esewaBrowserBackReleasedText",
                "Payment not completed. Your slot is still available. Please try again.",
              ),
              {
                autoClose: 4500,
              },
            );
          } else if (selectionState === "unavailable") {
            setErrorMessage(
              t(
                "esewaRetrySlotUnavailableText",
                "One or more selected slots are no longer available. Please choose another time. Your contact details are saved.",
              ),
            );
          } else if (selectionState === "unknown") {
            setErrorMessage(
              t(
                "esewaRetryAvailabilityUnknownText",
                "We could not refresh slot availability. Please choose your slot again. Your contact details are saved.",
              ),
            );
          } else {
            toast.info(
              t(
                "esewaRetryChooseSlotText",
                "Previous payment attempt ended. Please choose an available slot to continue.",
              ),
              {
                autoClose: 4500,
              },
            );
          }
        }

        return "abandoned";
      } catch (error) {
        if (error?.response?.status === 404) {
          if (transactionUuid) {
            clearEsewaTransactionSubmitted(
              window.sessionStorage,
              transactionUuid,
            );
          }
          clearEsewaRecoverySession(
            window.sessionStorage,
            reservationId,
          );
          paymentStartedRef.current = false;
          paymentRequestInFlightRef.current = false;
          setEsewaRecoveryState("idle");
          setPaymentOrder(null);
          setSuccessHold(null);
          setHoldSecondsRemaining(null);
          setSelectedSlotsOpen(false);

          const selectionState =
            await refreshSelectionAfterEsewaRelease();

          if (!silent) {
            if (selectionState === "available") {
              toast.error(
                t(
                  "esewaMissingRetryReadyText",
                  "Payment not completed. Your slot is still available. Please try again.",
                ),
                {
                  autoClose: 4500,
                },
              );
            } else if (selectionState === "unavailable") {
              setErrorMessage(
                t(
                  "esewaRetrySlotUnavailableText",
                  "One or more selected slots are no longer available. Please choose another time. Your contact details are saved.",
                ),
              );
            } else if (selectionState === "unknown") {
              setErrorMessage(
                t(
                  "esewaRetryAvailabilityUnknownText",
                  "We could not refresh slot availability. Please choose your slot again. Your contact details are saved.",
                ),
              );
            }
          }

          return "abandoned";
        }

        paymentStartedRef.current = true;
        setEsewaRecoveryState("unknown");

        if (!silent) {
          toast.info(
            t(
              "esewaAbandonUnknownText",
              "We could not confirm that the previous booking was released. Please do not start another payment yet; try again in a moment.",
            ),
            {
              position: "top-center",
              autoClose: 5200,
            },
          );
        }

        return "unknown";
      } finally {
        setEsewaAbandonmentLoading(false);
        esewaAbandonmentRef.current = false;
        releaseInFlightRef.current = false;
      }
    },
    [
      open,
      refreshSelectionAfterEsewaRelease,
      setErrorMessage,
      slug,
      successHold,
      t,
    ],
  );

  // Payment-result pages are a full route render, so browser pageshow/focus
  // events cannot be relied on to start recovery. Wait until the modal has
  // restored the sessionStorage booking snapshot, then explicitly run the same
  // manager-approved eSewa abandonment/retry policy used by browser Back.
  useEffect(() => {
    if (!open || !resumeEsewaRecovery) {
      return;
    }

    const recovery =
      getEsewaRecoveryContext(
        window.sessionStorage,
      );
    const hasMatchingRecovery = Boolean(
      recovery?.slug === slug &&
      recovery?.reservationId &&
      recovery?.releaseToken,
    );

    if (!hasMatchingRecovery) {
      onEsewaRecoveryHandled?.();
      return;
    }

    // The modal's initialization effect restores customer/slot state and moves
    // recovery to "checking". Waiting for that render prevents the recovery
    // request from running against empty initial React state.
    if (esewaRecoveryState !== "checking") {
      return;
    }

    onEsewaRecoveryHandled?.();

    const resumeEsewa = async () => {
      const state = await abandonEsewaForRetry({
        silent: false,
      });

      if (
        recoveryIntent === "choose_time" &&
        state === "abandoned"
      ) {
        setSelectedItems([]);
        setPromoPreview(null);
        setSelectedSlotsOpen(false);
        setStep(1);
        setSlotRefreshKey((value) => value + 1);
      }
    };

    resumeEsewa();
  }, [
    abandonEsewaForRetry,
    esewaRecoveryState,
    onEsewaRecoveryHandled,
    open,
    recoveryIntent,
    resumeEsewaRecovery,
    slug,
  ]);

  const reconcileEsewaPayment = useCallback(
    async ({ silent = false } = {}) => {
      if (
        !open ||
        !slug ||
        reconciliationInFlightRef.current ||
        releaseInFlightRef.current ||
        esewaAbandonmentRef.current
      ) {
        return;
      }

      const recovery =
        getEsewaRecoveryContext(
          window.sessionStorage,
        );

      if (
        !recovery ||
        recovery.slug !== slug
      ) {
        return;
      }

      reconciliationInFlightRef.current = true;
      esewaSubmittingRef.current = false;
      setEsewaRedirecting(false);
      setEsewaRecoveryState("checking");

      try {
        const data =
          await getPublicEsewaPaymentStatus(
            slug,
            recovery.reservationId,
            recovery.releaseToken,
          );

        if (esewaAbandonmentRef.current) {
          return;
        }

        const state = String(
          data?.state || "",
        ).toLowerCase();

        const expiresAt =
          data?.expiresAt ||
          recovery.expiresAt ||
          null;
        const reconciledTransactionUuid = String(
          data?.transactionUuid ||
            recovery.transactionUuid ||
            "",
        ).trim();

        if (
          reconciledTransactionUuid !==
          recovery.transactionUuid
        ) {
          saveEsewaRecoveryContext(
            window.sessionStorage,
            {
              ...recovery,
              transactionUuid:
                reconciledTransactionUuid,
              expiresAt,
            },
          );
        }

        if (state === "completed") {
          if (reconciledTransactionUuid) {
            clearEsewaTransactionSubmitted(
              window.sessionStorage,
              reconciledTransactionUuid,
            );
          }
          clearEsewaRecoveryContext(
            window.sessionStorage,
          );

          setEsewaRecoveryState("completed");

          if (data?.redirectUrl) {
            window.location.replace(
              data.redirectUrl,
            );
          }

          return;
        }

        if (state === "abandoned_paid") {
          if (reconciledTransactionUuid) {
            clearEsewaTransactionSubmitted(
              window.sessionStorage,
              reconciledTransactionUuid,
            );
          }
          clearEsewaRecoverySession(
            window.sessionStorage,
            recovery.reservationId,
          );
          paymentStartedRef.current = false;
          setEsewaRecoveryState("idle");
          setPaymentOrder(null);
          setSuccessHold(null);
          setHoldSecondsRemaining(null);
          setSelectedSlotsOpen(false);
          setSelectedItems([]);
          setPromoPreview(null);
          setStep(1);
          setSlotRefreshKey(
            (value) => value + 1,
          );

          toast.error(
            t(
              "esewaAbandonedLatePaymentText",
              "Payment was received for a booking attempt that had already been released. Do not pay again. Please contact support with your eSewa transaction reference.",
            ),
            {
              position: "top-center",
              autoClose: 7000,
            },
          );

          return;
        }

        if (
          state === "abandoned_pending" ||
          state === "abandoned_failed"
        ) {
          if (reconciledTransactionUuid) {
            clearEsewaTransactionSubmitted(
              window.sessionStorage,
              reconciledTransactionUuid,
            );
          }
          clearEsewaRecoverySession(
            window.sessionStorage,
            recovery.reservationId,
          );
          paymentStartedRef.current = false;
          setEsewaRecoveryState("idle");
          setPaymentOrder(null);
          setSuccessHold(null);
          setHoldSecondsRemaining(null);
          setSelectedSlotsOpen(false);
          setStep(
            selectedItems.length ? 3 : 1,
          );
          setSlotRefreshKey(
            (value) => value + 1,
          );

          if (!silent) {
            toast.info(
              t(
                "esewaAbandonedRetryReadyText",
                "No payment was confirmed for the previous booking attempt. The old slot hold is released, and you can try Pay via eSewa again.",
              ),
              {
                position: "top-center",
                autoClose: 5200,
              },
            );
          }

          return;
        }

        if (state === "pending") {
          paymentStartedRef.current = true;
          setEsewaRecoveryState("pending");
          setPaymentOrder(null);
          setStep(3);

          setSuccessHold((current) =>
            current || {
              _id: recovery.reservationId,
              releaseToken:
                recovery.releaseToken,
              expiresAt,
            },
          );

          if (expiresAt) {
            setHoldSecondsRemaining(
              getHoldSecondsRemaining(
                expiresAt,
              ),
            );
          }

          if (!silent) {
            toast.info(
              t(
                "esewaStillPendingText",
                "Your eSewa payment is still pending. Please don’t start another payment; you can check the status again shortly.",
              ),
              {
                position: "top-center",
                autoClose: 4200,
              },
            );
          }

          return;
        }

        if (
          state === "failed" ||
          state === "not_started"
        ) {
          if (reconciledTransactionUuid) {
            clearEsewaTransactionSubmitted(
              window.sessionStorage,
              reconciledTransactionUuid,
            );
          }

          paymentStartedRef.current = false;
          setEsewaRecoveryState("idle");
          setPaymentOrder(null);

          // A terminal non-success eSewa result must not reuse the old browser
          // hold. Release it first, then verify the selected slots again. The
          // next Pay click will create a fresh hold and run the server's atomic
          // conflict check again.
          let releasedForRetry = false;

          try {
            await releasePublicBookingHold(
              slug,
              recovery.reservationId,
              recovery.releaseToken,
            );
            releasedForRetry = true;
          } catch (releaseError) {
            if (releaseError?.response?.status === 404) {
              releasedForRetry = true;
            } else {
              paymentStartedRef.current = true;
              setEsewaRecoveryState("unknown");

              toast.info(
                t(
                  "esewaReleaseNeedsRecheckText",
                  "The previous eSewa session could not be released safely yet. We’ll keep checking before another payment is allowed.",
                ),
                {
                  position: "top-center",
                  autoClose: 4500,
                },
              );

              return;
            }
          }

          if (!releasedForRetry) {
            return;
          }

          clearEsewaRecoverySession(
            window.sessionStorage,
            recovery.reservationId,
          );

          setSuccessHold(null);
          setHoldSecondsRemaining(null);
          setPaymentOrder(null);
          setSelectedSlotsOpen(false);

          const selectionState =
            await refreshSelectionAfterEsewaRelease();

          if (selectionState === "available") {
            toast.info(
              t(
                "esewaRetryReleasedReadyText",
                "The eSewa payment was not completed. The previous hold was released, and your selected slot is still available. You can try Pay via eSewa again.",
              ),
              {
                position: "top-center",
                autoClose: 4800,
              },
            );

            return;
          }

          if (selectionState === "unavailable") {
            toast.info(
              t(
                "esewaRetrySelectionUnavailableText",
                "The eSewa payment was not completed, and one or more selected slots are no longer available. Please select another slot.",
              ),
              {
                position: "top-center",
                autoClose: 5000,
              },
            );

            return;
          }

          if (selectionState === "unknown") {
            toast.info(
              t(
                "esewaRetryAvailabilityUnknownText",
                "The eSewa payment was not completed and the previous hold was released, but availability could not be refreshed. Please select a slot again.",
              ),
              {
                position: "top-center",
                autoClose: 5000,
              },
            );

            return;
          }

          toast.info(
            t(
              "esewaSessionEndedText",
              "That payment session has ended. Please choose a slot again — your contact details are saved.",
            ),
            {
              position: "top-center",
              autoClose: 4200,
            },
          );

          return;
        }

        setEsewaRecoveryState("unknown");
      } catch (error) {
        if (
          error?.response?.status === 404
        ) {
          const recovery =
            getEsewaRecoveryContext(
              window.sessionStorage,
            );

          if (recovery?.transactionUuid) {
            clearEsewaTransactionSubmitted(
              window.sessionStorage,
              recovery.transactionUuid,
            );
          }

          clearEsewaRecoverySession(
            window.sessionStorage,
            recovery?.reservationId || "",
          );
          paymentStartedRef.current = false;
          setEsewaRecoveryState("idle");
          setPaymentOrder(null);
          setSuccessHold(null);
          setHoldSecondsRemaining(null);
          setSelectedSlotsOpen(false);
          setSlotRefreshKey(
            (value) => value + 1,
          );

          if (selectedItems.length) {
            setStep(3);
            toast.info(
              t(
                "esewaMissingRetryReadyText",
                "The previous payment session is no longer active. You can try payment again; slot availability will be checked first.",
              ),
              {
                position: "top-center",
                autoClose: 4500,
              },
            );
          } else {
            setStep(1);

            if (!silent) {
              setErrorMessage(
                t(
                  "esewaSessionMissingText",
                  "The previous payment session is no longer active. Please choose a slot again.",
                ),
              );
            }
          }

          return;
        }

        paymentStartedRef.current = true;
        setEsewaRecoveryState("unknown");

        if (!silent) {
          toast.info(
            t(
              "esewaStatusUnknownText",
              "We couldn’t confirm the eSewa status yet. Please don’t submit another payment; try checking again in a moment.",
            ),
            {
              position: "top-center",
              autoClose: 4500,
            },
          );
        }
      } finally {
        reconciliationInFlightRef.current = false;
      }
    },
    [
      open,
      refreshSelectionAfterEsewaRelease,
      setErrorMessage,
      slug,
      t,
    ],
  );

  useEffect(() => {
    if (
      !open ||
      holdSecondsRemaining !== 0 ||
      !successHold?._id
    ) {
      return;
    }

    const recovery =
      getEsewaRecoveryContext(
        window.sessionStorage,
      );

    if (
      recovery?.reservationId &&
      String(recovery.reservationId) ===
        String(successHold._id)
    ) {
      reconcileEsewaPayment({
        silent: true,
      });
    }
  }, [
    open,
    holdSecondsRemaining,
    successHold?._id,
    reconcileEsewaPayment,
  ]);

  // A submitted eSewa form returning to this page is an explicit browser
  // abandonment for Venue Public. The first resume signal releases the old
  // hold through the dedicated server endpoint; duplicate resume events are
  // serialized by releaseInFlightRef.
  useEffect(() => {
    if (!open) return undefined;

    const handleResume = () => {
      if (document.visibilityState === "hidden") {
        return;
      }

      const recovery =
        getEsewaRecoveryContext(
          window.sessionStorage,
        );
      const transactionUuid = String(
        recovery?.slug === slug
          ? recovery?.transactionUuid || ""
          : "",
      ).trim();
      const submittedEsewaAttempt = Boolean(
        transactionUuid &&
          wasEsewaTransactionSubmitted(
            window.sessionStorage,
            transactionUuid,
          ),
      );

      if (submittedEsewaAttempt) {
        abandonEsewaForRetry({
          silent: false,
        });
        return;
      }

      if (successHold?.expiresAt) {
        setHoldSecondsRemaining(
          getHoldSecondsRemaining(
            successHold.expiresAt,
          ),
        );
      }

      reconcileEsewaPayment({
        silent: true,
      });
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState ===
        "visible"
      ) {
        handleResume();
      }
    };

    window.addEventListener(
      "pageshow",
      handleResume,
    );
    window.addEventListener(
      "focus",
      handleResume,
    );
    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange,
    );

    return () => {
      window.removeEventListener(
        "pageshow",
        handleResume,
      );
      window.removeEventListener(
        "focus",
        handleResume,
      );
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );
    };
  }, [
    abandonEsewaForRetry,
    open,
    reconcileEsewaPayment,
    slug,
    successHold?.expiresAt,
  ]);

  // On a full history restoration the app can mount after the browser's
  // pageshow event. Navigation Timing provides the standards-based fallback.
  useEffect(() => {
    if (
      !open ||
      typeof performance === "undefined" ||
      typeof performance.getEntriesByType !==
        "function"
    ) {
      return;
    }

    const navigationEntry =
      performance.getEntriesByType(
        "navigation",
      )?.[0];

    if (
      navigationEntry?.type !==
      "back_forward"
    ) {
      return;
    }

    const recovery =
      getEsewaRecoveryContext(
        window.sessionStorage,
      );
    const transactionUuid = String(
      recovery?.slug === slug
        ? recovery?.transactionUuid || ""
        : "",
    ).trim();

    if (
      !transactionUuid ||
      !wasEsewaTransactionSubmitted(
        window.sessionStorage,
        transactionUuid,
      )
    ) {
      return;
    }

    abandonEsewaForRetry({
      silent: false,
    });
  }, [
    abandonEsewaForRetry,
    open,
    slug,
  ]);

  const submitEsewaPayment = (order, reservation = successHold) => {
    if (
      esewaSubmittingRef.current ||
      esewaRedirecting
    ) {
      return;
    }

    const esewa = order?.esewa?.web || order?.esewa;

    if (!esewa?.endpoint || !esewa?.params) {
      setErrorMessage(
        t(
          "missingEsewaDetailsText",
          "eSewa payment details are missing.",
        ),
      );
      return;
    }

    const transactionUuid = String(
      esewa.params.transaction_uuid || "",
    ).trim();

    if (!transactionUuid) {
      setErrorMessage(
        t(
          "missingEsewaTransactionText",
          "The eSewa transaction reference is missing. Please create a new payment request.",
        ),
      );
      setPaymentOrder(null);
      return;
    }

    if (
      wasEsewaTransactionSubmitted(
        window.sessionStorage,
        transactionUuid,
      )
    ) {
      setEsewaRecoveryState("checking");
      reconcileEsewaPayment({
        silent: false,
      });
      return;
    }

    const form = document.createElement("form");

    try {
      esewaSubmittingRef.current = true;
      setEsewaRedirecting(true);
      setErrorMessage("");

      saveEsewaRecoveryContext(
        window.sessionStorage,
        {
          slug,
          reservationId:
            reservation?._id ||
            order?.reservationId ||
            order?.bookingId,
          releaseToken:
            reservation?.releaseToken,
          transactionUuid,
          expiresAt:
            reservation?.expiresAt ||
            null,
          startedAt: Date.now(),
          bookingSnapshot: {
            customer,
            phoneCountryCode,
            selectedGroundId,
            selectedDate,
            selectedItems,
            promoCode,
            promoPreview,
          },
        },
      );

      markEsewaTransactionSubmitted(
        window.sessionStorage,
        transactionUuid,
      );

      setEsewaRecoveryState("pending");

      form.method = "POST";
      form.action = esewa.endpoint;
      form.target = "_self";
      form.style.display = "none";

      Object.entries(esewa.params).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      form.remove();
      esewaSubmittingRef.current = false;
      setEsewaRedirecting(false);

      clearEsewaTransactionSubmitted(
        window.sessionStorage,
        transactionUuid,
      );
      setEsewaRecoveryState("checking");

      setErrorMessage(
        error?.message ||
        t(
          "esewaRedirectErrorText",
          "eSewa did not open. We’re checking the payment session before allowing another attempt.",
        ),
      );

      reconcileEsewaPayment({
        silent: true,
      });
    }
  };

  const getRazorpayBookingSnapshot = useCallback(
    () => ({
      customer,
      phoneCountryCode,
      selectedGroundId,
      selectedDate,
      selectedItems,
      promoCode,
      promoPreview,
    }),
    [
      customer,
      phoneCountryCode,
      promoCode,
      promoPreview,
      selectedDate,
      selectedGroundId,
      selectedItems,
    ],
  );

  const persistRazorpayRecovery = useCallback(
    ({
      order,
      reservation,
      state = "ready",
      verificationPayload = null,
    }) =>
      saveRazorpayRecoveryContext(
        window.sessionStorage,
        {
          slug,
          reservationId:
            reservation?._id ||
            order?.reservationId ||
            order?.bookingId,
          releaseToken:
            reservation?.releaseToken,
          expiresAt:
            reservation?.expiresAt || null,
          startedAt: Date.now(),
          state,
          paymentOrder: order,
          verificationPayload,
          bookingSnapshot:
            getRazorpayBookingSnapshot(),
        },
      ),
    [getRazorpayBookingSnapshot, slug],
  );

  const buildRazorpayResultDetails = useCallback(
    (finalReservation = null) => {
      const bookingWhen = selectedItemGroups
        .map(({ date, items }) => {
          const slotText = items
            .map(
              (item) =>
                `${formatSlotTime(item.start)} \u2013 ${formatSlotTime(item.end)}`,
            )
            .join(", ");

          return [formatBookingDate(date), slotText]
            .filter(Boolean)
            .join(" \u00b7 ");
        })
        .filter(Boolean)
        .join("; ");

      const resultSummary =
        getPublicBookingSummary({
          selectedItemsTotal,
          reservation:
            finalReservation ||
            successHold ||
            previewReservation,
        });

      const resultCurrency = String(
        finalReservation?.currency ||
          finalReservation?.paymentCurrency ||
          selectedItems[0]?.currency ||
          bookingCurrency ||
          "INR",
      )
        .trim()
        .toUpperCase();

      return {
        bookingReference:
          finalReservation?.generatedReservationId ||
          finalReservation?.reservationId ||
          finalReservation?._id ||
          "",
        groundName:
          finalReservation?.groundName ||
          selectedGroundName ||
          "",
        bookingWhen,
        slots: selectedItems.map((item) => ({
          date: item.date,
          start: item.start,
          end: item.end,
          price: getPublicWebsiteSlotAmount(
            item,
            0,
          ),
          currency:
            item?.currency ||
            resultCurrency,
        })),
        currency: resultCurrency,
        pricing: resultSummary,
        promoCode:
          resultSummary.promoCode ||
          promoPreview?.code ||
          "",
      };
    },
    [
      bookingCurrency,
      previewReservation,
      promoPreview?.code,
      selectedGroundName,
      selectedItemGroups,
      selectedItems,
      selectedItemsTotal,
      successHold,
    ],
  );

  const goToRazorpayResult = useCallback(
    (resultType, details = {}) => {
      const isSuccessResult = resultType === "success";
      const search = buildRazorpayResultSearch({
        resultType,
        bookingReference: isSuccessResult
          ? ""
          : details.bookingReference || "",
        groundName: isSuccessResult
          ? ""
          : details.groundName || "",
        bookingWhen: isSuccessResult
          ? ""
          : details.bookingWhen || "",
      });

      if (!search) return;

      const params =
        new URLSearchParams(search);
      const resultSlots =
        Array.isArray(details.slots)
          ? details.slots.slice(0, 24)
          : [];
      const pricing =
        details.pricing &&
        typeof details.pricing === "object"
          ? details.pricing
          : {};

      if (!isSuccessResult && resultSlots.length) {
        params.set(
          "booking_slots",
          JSON.stringify(resultSlots),
        );
      }

      if (!isSuccessResult && details.currency) {
        params.set(
          "booking_currency",
          String(details.currency)
            .trim()
            .slice(0, 12),
        );
      }

      [
        ["booking_subtotal", pricing.subtotal],
        ["booking_discount", pricing.discountAmount],
        ["booking_total", pricing.finalAmount],
      ].forEach(([key, value]) => {
        const amount = Number(value);

        if (!isSuccessResult && Number.isFinite(amount)) {
          params.set(key, String(amount));
        }
      });

      const promoCode = String(
        details.promoCode ||
          pricing.promoCode ||
          "",
      )
        .trim()
        .toUpperCase();

      if (!isSuccessResult && promoCode) {
        params.set(
          "booking_promo",
          promoCode.slice(0, 80),
        );
      }

      const failureReason = String(
        details.failureReason || "",
      ).trim();

      if (failureReason) {
        params.set(
          "payment_reason",
          failureReason.slice(0, 180),
        );
      }

      const resultUrl = new URL(
        window.location.href,
      );
      resultUrl.search =
        `?${params.toString()}`;
      resultUrl.hash = "";
      window.location.assign(
        resultUrl.toString(),
      );
    },
    [],
  );

  const verifyRazorpayPaymentPayload = useCallback(
    async (payload, context = {}) => {
      const recoveryOrder =
        context.order || paymentOrder;
      const recoveryReservation =
        context.reservation || successHold;

      setRazorpayVerifying(true);
      setErrorMessage("");

      try {
        const verified =
          await verifyReservationRazorpayPayment(payload);
        const finalReservation =
          verified?.finalReservation || null;

        if (
          !finalReservation ||
          finalReservation.reservationStatus !==
            "confirmed" ||
          finalReservation.paymentStatus !==
            "completed"
        ) {
          throw new Error(
            t(
              "razorpayVerificationIncompleteText",
              "Payment was received but booking confirmation is still pending. Please do not pay again.",
            ),
          );
        }

        setRazorpayResult(finalReservation);
        setRazorpayVerificationPayload(null);
        setRazorpayPaymentPending(false);
        setHoldSecondsRemaining(null);
        paymentStartedRef.current = false;
        setSlotRefreshKey((value) => value + 1);

        clearRazorpayRecoveryContext(
          window.sessionStorage,
        );

        toast.success(
          t(
            "razorpayPaymentSuccessText",
            "Payment verified. Your booking is confirmed.",
          ),
          {
            position: "top-center",
            autoClose: 4200,
          },
        );

        goToRazorpayResult(
          "success",
          buildRazorpayResultDetails(
            finalReservation,
          ),
        );

        return finalReservation;
      } catch (error) {
        // Once Checkout returns a successful payment payload, never create or
        // reopen another payment attempt until this exact signed payload has
        // reached a terminal server result.
        setRazorpayVerificationPayload(payload);
        setRazorpayPaymentPending(true);

        const responseData =
          error?.response?.data || {};
        const status = Number(
          error?.response?.status || 0,
        );
        const manualReviewRequired = Boolean(
          responseData.manualReviewRequired,
        );
        const paymentPending = Boolean(
          responseData.paymentPending,
        );
        const retrySameVerification = Boolean(
          paymentPending ||
            !status ||
            status >= 500,
        );

        const message =
          responseData.message ||
          error?.message ||
          (retrySameVerification
            ? t(
                "razorpayCapturePendingText",
                "Your payment is being confirmed. Do not pay again; return to the booking to check the same payment status.",
              )
            : t(
                "razorpayVerificationErrorText",
                "We could not safely confirm the Razorpay payment. Do not pay again; contact support if the amount was debited.",
              ));

        setErrorMessage(message);

        if (manualReviewRequired) {
          paymentStartedRef.current = false;
          clearRazorpayRecoveryContext(
            window.sessionStorage,
          );
          goToRazorpayResult("error");
          return null;
        }

        if (retrySameVerification) {
          persistRazorpayRecovery({
            order: recoveryOrder,
            reservation: recoveryReservation,
            state: "pending",
            verificationPayload: payload,
          });
          goToRazorpayResult(
            "pending",
            buildRazorpayResultDetails(),
          );
          return null;
        }

        clearRazorpayRecoveryContext(
          window.sessionStorage,
        );
        goToRazorpayResult("error");
        return null;
      } finally {
        setRazorpayVerifying(false);
      }
    },
    [
      buildRazorpayResultDetails,
      goToRazorpayResult,
      paymentOrder,
      persistRazorpayRecovery,
      setErrorMessage,
      successHold,
      t,
    ],
  );

  const openRazorpayCheckout = useCallback(
    async (order, reservationToPay) => {
      const key = String(
        order?.razorpayKeyId || "",
      ).trim();
      const orderId = String(
        order?.razorpayOrderId || "",
      ).trim();
      const amount = Number(order?.amount || 0);
      const currency = String(
        order?.currency || bookingCurrency || "INR",
      ).toUpperCase();

      if (!key || !orderId || amount <= 0) {
        throw new Error(
          t(
            "razorpayOrderInvalidText",
            "Razorpay payment details are incomplete. Please try again.",
          ),
        );
      }

      persistRazorpayRecovery({
        order,
        reservation: reservationToPay,
        state: "ready",
      });

      const RazorpayCheckout =
        await loadRazorpayCheckout();

      const checkout = new RazorpayCheckout({
        key,
        amount,
        currency,
        order_id: orderId,
        name: venueName,
        description: selectedGroundName,
        prefill: {
          name: customer.userName || "",
          email: customer.email || "",
          contact:
            buildPublicBookingContactNumber(
              customer.contactNumber,
              phoneCountryCode,
            ) || "",
        },
        notes: {
          reservationId:
            reservationToPay?._id || "",
        },
        theme: {
          color: primaryColor,
        },
        // Keep Razorpay Checkout bounded by the same browser hold window.
        // The backend remains the source of truth if browser timers are paused.
        timeout:
          Number(holdSecondsRemaining) > 0
            ? Math.max(1, Math.floor(Number(holdSecondsRemaining)))
            : undefined,
        // Keep retry ownership in Onground so failure always follows the same
        // result-page -> safe release -> slot recheck -> Pay-again journey as
        // eSewa instead of retrying invisibly inside the provider modal.
        retry: { enabled: false },
        handler: async (response) => {
          const verificationPayload = {
            razorpayOrderId:
              response?.razorpay_order_id ||
              orderId,
            razorpayPaymentId:
              response?.razorpay_payment_id,
            razorpaySignature:
              response?.razorpay_signature,
          };

          setRazorpayVerificationPayload(
            verificationPayload,
          );
          setRazorpayPaymentPending(true);

          persistRazorpayRecovery({
            order,
            reservation: reservationToPay,
            state: "pending",
            verificationPayload,
          });

          await verifyRazorpayPaymentPayload(
            verificationPayload,
            {
              order,
              reservation: reservationToPay,
            },
          );
        },
        modal: {
          ondismiss: () => {
            const recovery =
              getRazorpayRecoveryContext(
                window.sessionStorage,
              );
            const resumeAction =
              getRazorpayRecoveryResumeAction(
                recovery,
              );

            // A plain Checkout exit is the Razorpay equivalent of returning
            // from eSewa before payment confirmation. Reconcile with the
            // provider, release only when safe, then re-check the slots before
            // enabling another Pay action. A failed/signed/pending attempt is
            // handled by its dedicated result/recovery path instead.
            if (resumeAction === "resume") {
              razorpayAbandonForRetryRef.current?.({
                silent: false,
                refreshSelection: true,
              });
            }
          },
        },
      });

      checkout.on(
        "payment.failed",
        (response) => {
          setErrorMessage(
            response?.error?.description ||
              t(
                "razorpayPaymentFailedText",
                "Razorpay could not complete this payment. Use Try Again to safely release the failed attempt, recheck your slot, and pay again.",
              ),
          );

          persistRazorpayRecovery({
            order,
            reservation: reservationToPay,
            state: "failed",
          });

          goToRazorpayResult(
            "failed",
            {
              ...buildRazorpayResultDetails(),
              failureReason:
                response?.error?.description ||
                t(
                  "razorpayPaymentFailedReasonFallback",
                  "Payment not completed",
                ),
            },
          );
        },
      );

      checkout.open();
    },
    [
      bookingCurrency,
      customer.contactNumber,
      customer.email,
      customer.userName,
      phoneCountryCode,
      primaryColor,
      selectedGroundName,
      buildRazorpayResultDetails,
      goToRazorpayResult,
      holdSecondsRemaining,
      persistRazorpayRecovery,
      setErrorMessage,
      t,
      venueName,
      verifyRazorpayPaymentPayload,
    ],
  );

  const handleRetryRazorpayPayment = useCallback(
    async () => {
      if (
        !paymentOrder ||
        razorpayVerifying ||
        paymentLoading ||
        (razorpayPaymentPending &&
          !razorpayVerificationPayload) ||
        (!razorpayVerificationPayload &&
          holdSecondsRemaining === 0)
      ) {
        return;
      }

      try {
        setPaymentLoading(true);
        setErrorMessage("");

        if (razorpayVerificationPayload) {
          await verifyRazorpayPaymentPayload(
            razorpayVerificationPayload,
          );
          return;
        }

        await openRazorpayCheckout(
          paymentOrder,
          successHold,
        );
      } catch (error) {
        setErrorMessage(
          error?.message ||
            t(
              "razorpayOpenErrorText",
              "Unable to open Razorpay Checkout.",
            ),
        );
      } finally {
        setPaymentLoading(false);
      }
    },
    [
      holdSecondsRemaining,
      openRazorpayCheckout,
      paymentLoading,
      paymentOrder,
      razorpayPaymentPending,
      razorpayVerificationPayload,
      razorpayVerifying,
      setErrorMessage,
      successHold,
      t,
      verifyRazorpayPaymentPayload,
    ],
  );

  const abandonRazorpayForRetry = useCallback(
    async ({ silent = false, refreshSelection = true } = {}) => {
      if (
        !open ||
        !slug ||
        releaseInFlightRef.current ||
        razorpayVerifying
      ) {
        return "busy";
      }

      const recovery =
        getRazorpayRecoveryContext(
          window.sessionStorage,
        );
      const recoveryMatchesSlug =
        recovery?.slug === slug;
      const reservationId = String(
        (recoveryMatchesSlug
          ? recovery?.reservationId
          : "") ||
          successHold?._id ||
          "",
      ).trim();
      const releaseToken = String(
        (recoveryMatchesSlug
          ? recovery?.releaseToken
          : "") ||
          successHold?.releaseToken ||
          "",
      ).trim();

      if (!reservationId || !releaseToken) {
        return "missing";
      }

      // A signed Checkout success payload must be re-verified, never abandoned.
      if (
        razorpayVerificationPayload ||
        recovery?.verificationPayload
      ) {
        return "processing";
      }

      releaseInFlightRef.current = true;
      setPaymentLoading(true);
      setRazorpayAbandonmentLoading(true);
      setErrorMessage("");

      try {
        const data =
          await abandonPublicRazorpayPayment(
            slug,
            reservationId,
            releaseToken,
          );
        const state = String(
          data?.state || "",
        ).toLowerCase();

        if (state === "completed") {
          clearRazorpayRecoveryContext(
            window.sessionStorage,
          );
          paymentStartedRef.current = false;
          setRazorpayPaymentPending(false);
          setRazorpayVerificationPayload(null);

          goToRazorpayResult(
            "success",
            buildRazorpayResultDetails(
              data?.reservation || null,
            ),
          );

          return "completed";
        }

        if (state === "abandoned_paid") {
          clearRazorpayRecoveryContext(
            window.sessionStorage,
          );
          paymentStartedRef.current = false;
          setRazorpayPaymentPending(false);
          setRazorpayVerificationPayload(null);
          setPaymentOrder(null);
          setSuccessHold(null);
          setHoldSecondsRemaining(null);
          setSelectedSlotsOpen(false);
          setSelectedItems([]);
          setPromoPreview(null);
          setStep(1);
          setSlotRefreshKey(
            (value) => value + 1,
          );

          toast.error(
            t(
              "razorpayAbandonedLatePaymentText",
              "Payment was received for a Razorpay attempt that was already being released. Do not pay again. Please contact support with your payment reference.",
            ),
            {
              position: "top-center",
              autoClose: 7000,
              toastId:
                "venue-public-razorpay-abandoned-paid",
            },
          );

          return "abandoned_paid";
        }

        if (state === "processing") {
          setRazorpayPaymentPending(true);

          if (recoveryMatchesSlug && recovery) {
            saveRazorpayRecoveryContext(
              window.sessionStorage,
              {
                ...recovery,
                state: "pending",
              },
            );
          }

          if (!silent) {
            toast.info(
              data?.message ||
                t(
                  "razorpayAbandonProcessingText",
                  "Your Razorpay payment is still being checked. Please do not pay again until the status is confirmed.",
                ),
              {
                position: "top-center",
                autoClose: 5200,
                toastId:
                  "venue-public-razorpay-abandon-processing",
              },
            );
          }

          return "processing";
        }

        if (state !== "abandoned") {
          throw new Error(
            data?.message ||
              "The previous Razorpay booking could not be released.",
          );
        }

        clearRazorpayRecoveryContext(
          window.sessionStorage,
        );
        paymentStartedRef.current = false;
        paymentRequestInFlightRef.current = false;
        setRazorpayPaymentPending(false);
        setRazorpayVerificationPayload(null);
        setRazorpayResult(null);
        setPaymentOrder(null);
        setSuccessHold(null);
        setHoldSecondsRemaining(null);
        setSelectedSlotsOpen(false);

        if (!refreshSelection) {
          return "abandoned";
        }

        const selectionState =
          await refreshSelectionAfterEsewaRelease();

        if (!silent) {
          if (selectionState === "available") {
            toast.error(
              t(
                "razorpayRetryReadyText",
                "Payment not completed. Your slot is still available. Please try again.",
              ),
              {
                autoClose: 4500,
                toastId:
                  "venue-public-razorpay-retry-ready",
              },
            );
          } else if (selectionState === "unavailable") {
            setErrorMessage(
              t(
                "razorpayRetrySlotUnavailableText",
                "One or more selected slots are no longer available. Please choose another time. Your contact details are saved.",
              ),
            );
          } else if (selectionState === "unknown") {
            setErrorMessage(
              t(
                "razorpayRetryAvailabilityUnknownText",
                "We could not refresh slot availability. Please choose your slot again. Your contact details are saved.",
              ),
            );
          } else {
            toast.info(
              t(
                "razorpayRetryChooseSlotText",
                "Previous payment attempt ended. Please choose an available slot to continue.",
              ),
              {
                autoClose: 4500,
                toastId:
                  "venue-public-razorpay-retry-choose-slot",
              },
            );
          }
        }

        return "abandoned";
      } catch (error) {
        setRazorpayPaymentPending(true);

        if (!silent) {
          toast.info(
            error?.response?.data?.message ||
              error?.message ||
              t(
                "razorpayAbandonUnknownText",
                "We could not confirm that the previous Razorpay booking was released. Please do not start another payment yet; try again in a moment.",
              ),
            {
              position: "top-center",
              autoClose: 5200,
              toastId:
                "venue-public-razorpay-abandon-unknown",
            },
          );
        }

        return "unknown";
      } finally {
        setPaymentLoading(false);
        setRazorpayAbandonmentLoading(false);
        releaseInFlightRef.current = false;
      }
    },
    [
      buildRazorpayResultDetails,
      goToRazorpayResult,
      open,
      razorpayVerificationPayload,
      razorpayVerifying,
      refreshSelectionAfterEsewaRelease,
      setErrorMessage,
      slug,
      successHold,
      t,
    ],
  );

  useEffect(() => {
    razorpayAbandonForRetryRef.current =
      abandonRazorpayForRetry;

    return () => {
      razorpayAbandonForRetryRef.current = null;
    };
  }, [abandonRazorpayForRetry]);

  useEffect(() => {
    if (!open || !resumeRazorpayRecovery) {
      return;
    }

    const recovery =
      getRazorpayRecoveryContext(
        window.sessionStorage,
      );
    const matchesCurrentVenue = Boolean(
      recovery?.slug === slug &&
        recovery?.reservationId &&
        recovery?.releaseToken,
    );

    if (!matchesCurrentVenue) {
      onRazorpayRecoveryHandled?.();
      return;
    }

    // Wait until the normal modal restore effect has populated the existing
    // Razorpay order before resolving the recovery request.
    if (!paymentOrder?.razorpayOrderId) {
      return;
    }

    onRazorpayRecoveryHandled?.();

    const resumeAction =
      getRazorpayRecoveryResumeAction(recovery);

    if (resumeAction === "verify") {
      handleRetryRazorpayPayment();
      return;
    }

    if (resumeAction === "abandon") {
      const resumeRazorpay = async () => {
        const state = await abandonRazorpayForRetry({
          silent: false,
          refreshSelection:
            recoveryIntent !== "choose_time",
        });

        if (
          recoveryIntent === "choose_time" &&
          state === "abandoned"
        ) {
          setSelectedItems([]);
          setPromoPreview(null);
          setSelectedSlotsOpen(false);
          setStep(1);
          setSlotRefreshKey((value) => value + 1);
        }
      };

      resumeRazorpay();
    }
  }, [
    abandonRazorpayForRetry,
    handleRetryRazorpayPayment,
    onRazorpayRecoveryHandled,
    open,
    paymentOrder?.razorpayOrderId,
    recoveryIntent,
    resumeRazorpayRecovery,
    slug,
  ]);

  const handleCreatePaymentOrder = async () => {
    if (
      paymentLoading ||
      esewaRedirecting ||
      promoApplying ||
      releaseInFlightRef.current ||
      esewaAbandonmentRef.current ||
      esewaRecoveryState === "pending" ||
      esewaRecoveryState === "checking" ||
      esewaRecoveryState === "unknown"
    ) {
      return;
    }

    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    let reservationToPay = successHold;
    let createdNow = false;
    let paymentCurrency = "";

    try {
      paymentRequestInFlightRef.current = true;
      paymentStartedRef.current = true;
      setPaymentLoading(true);
      setErrorMessage("");

      // Selection, details, review and promo preview do not create a
      // reservation. The first Pay click creates the hold. After any released
      // eSewa attempt (including browser Back), retry reaches this path with no
      // successHold and creates a fresh server-checked hold + fresh timer.
      if (!reservationToPay?._id) {
        const holdData =
          await createPublicBookingHold(
            slug,
            buildBookingPayload(
              promoPreview?.code || "",
            ),
            { slotSize },
          );

        reservationToPay =
          holdData?.reservation || null;
        createdNow = true;

        if (!reservationToPay?._id) {
          throw new Error(
            t(
              "holdCreateErrorText",
              "Unable to prepare your booking.",
            ),
          );
        }

        setSuccessHold(reservationToPay);
        setHoldSecondsRemaining(
          getHoldSecondsRemaining(
            reservationToPay.expiresAt,
          ),
        );
        onHoldCreated?.(
          reservationToPay,
          holdData,
        );
      }

      if (
        isHoldExpired(
          reservationToPay?.expiresAt,
        )
      ) {
        throw new Error(
          t(
            "holdExpiredText",
            "Your payment time has expired. Please select the slot again.",
          ),
        );
      }

      paymentCurrency = String(
        selectedItems[0]?.currency ||
          venue?.pricing?.currency ||
          "",
      ).toUpperCase();

      const confirmationContextSaved =
        savePublicPaymentConfirmationContext(
          window.sessionStorage,
          {
            slug,
            reservationId: reservationToPay._id,
            confirmationToken: reservationToPay.releaseToken,
            paymentGateway:
              paymentCurrency === "NPR" ? "ESEWA" : "RAZORPAY",
          },
        );

      if (!confirmationContextSaved) {
        throw new Error(
          t(
            "paymentConfirmationSessionErrorText",
            "Unable to create a secure payment confirmation session. Please reload and try again before paying.",
          ),
        );
      }

      if (paymentCurrency === "NPR") {
        // Persist the reservation capability before asking the server to create
        // the eSewa attempt. If the response is lost after the server commits
        // the attempt, a reload/back navigation can still reconcile safely.
        saveEsewaRecoveryContext(
          window.sessionStorage,
          {
            slug,
            reservationId: reservationToPay._id,
            releaseToken: reservationToPay.releaseToken,
            transactionUuid: "",
            expiresAt: reservationToPay.expiresAt || null,
            startedAt: Date.now(),
            bookingSnapshot: {
              customer,
              phoneCountryCode,
              selectedGroundId,
              selectedDate,
              selectedItems,
              promoCode,
              promoPreview,
            },
          },
        );
      } else {
        // Mirror eSewa's pre-request recovery capability. If the server creates
        // and saves a Razorpay order but the HTTP response is lost, the same
        // reservation can recover that provider order instead of creating a
        // second one.
        persistRazorpayRecovery({
          order: null,
          reservation: reservationToPay,
          state: "preparing",
        });
      }

      const data =
        paymentCurrency === "NPR"
          ? await createEsewaWebV2PaymentOrder(
              reservationToPay._id,
            )
          : await createReservationPaymentOrder(
              reservationToPay._id,
            );

      if (paymentCurrency === "NPR") {
        submitEsewaPayment(
          data,
          reservationToPay,
        );
      } else {
        setPaymentOrder(data);
        await openRazorpayCheckout(
          data,
          reservationToPay,
        );
      }
    } catch (error) {
      paymentStartedRef.current = false;
      setEsewaRecoveryState("idle");

      if (
        createdNow &&
        reservationToPay?._id &&
        reservationToPay?.releaseToken
      ) {
        let releasedAfterFailure = false;

        try {
          await releasePublicBookingHold(
            slug,
            reservationToPay._id,
            reservationToPay.releaseToken,
          );
          releasedAfterFailure = true;
          clearEsewaRecoveryContext(
            window.sessionStorage,
          );
          clearRazorpayRecoveryContext(
            window.sessionStorage,
          );
          setSlotRefreshKey(
            (value) => value + 1,
          );
        } catch (_releaseError) {
          // A lost network response can mean the server already committed the
          // gateway payment-start state. Never create a fresh reservation or
          // discard the recovery capability until that provider state is known.
          if (paymentCurrency === "NPR") {
            paymentStartedRef.current = true;
            setSuccessHold(reservationToPay);
            setEsewaRecoveryState("checking");
            setHoldSecondsRemaining(
              getHoldSecondsRemaining(
                reservationToPay.expiresAt,
              ),
            );

            await reconcileEsewaPayment({
              silent: false,
            });
            return;
          }

          const initialStatus = Number(
            error?.response?.status || 0,
          );
          const canRecoverLostRazorpayResponse =
            paymentCurrency !== "NPR" &&
            (!initialStatus || initialStatus >= 500);

          if (canRecoverLostRazorpayResponse) {
            paymentStartedRef.current = true;
            setSuccessHold(reservationToPay);
            setHoldSecondsRemaining(
              getHoldSecondsRemaining(
                reservationToPay.expiresAt,
              ),
            );

            try {
              const recoveredOrder =
                await createReservationPaymentOrder(
                  reservationToPay._id,
                );

              setPaymentOrder(recoveredOrder);
              persistRazorpayRecovery({
                order: recoveredOrder,
                reservation: reservationToPay,
                state: "ready",
              });

              await openRazorpayCheckout(
                recoveredOrder,
                reservationToPay,
              );
              return;
            } catch (recoveryError) {
              const recoveryData =
                recoveryError?.response?.data || {};

              setErrorMessage(
                recoveryData.message ||
                  recoveryError?.message ||
                  t(
                    "razorpayRecoveryUnavailableText",
                    "The Razorpay payment session could not be recovered yet. Do not start another payment; try again shortly.",
                  ),
              );
              return;
            }
          }
        }

        if (releasedAfterFailure) {
          setSuccessHold(null);
          setHoldSecondsRemaining(null);
        }
      }

      if (isSlotConflictError(error)) {
        setStep(1);
        setSelectedItems([]);
        setSlots([]);
        setPromoPreview(null);
        setErrorMessage(
          t(
            "slotConflictText",
            "That slot was just booked. Please choose another time — your contact details are saved.",
          ),
        );
        setLoadingSlots(true);

        requestAnimationFrame(() => {
          bookingContentRef.current
            ?.closest(".rm-body")
            ?.scrollTo({ top: 0 });
        });

        try {
          const data =
            await getPublicGroundSlots(
              slug,
              selectedGroundId,
              selectedDate,
              slotSize,
              timezone,
            );

          setSlots(
            Array.isArray(data?.slots)
              ? data.slots
              : [],
          );
        } catch {
          setErrorMessage(
            t(
              "slotRefreshErrorText",
              "That slot was just booked. Availability could not refresh automatically; please choose the date again. Your contact details are saved.",
            ),
          );
        } finally {
          setLoadingSlots(false);
        }

        return;
      }

      if (promoPreview && createdNow) {
        setPromoPreview(null);
      }

      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          t(
            "paymentOrderErrorText",
            "Unable to create payment order.",
          ),
      );
    } finally {
      paymentRequestInFlightRef.current = false;
      setPaymentLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setErrorMessage("");
    setStep(3);

    requestAnimationFrame(() => {
      bookingContentRef.current
        ?.closest(".rm-body")
        ?.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const handleClose = useCallback(async () => {
    if (
      paymentRequestInFlightRef.current ||
      paymentLoading ||
      esewaRedirecting ||
      esewaSubmittingRef.current ||
      releaseInFlightRef.current ||
      esewaAbandonmentRef.current
    ) {
      return;
    }

    const recovery =
      getEsewaRecoveryContext(
        window.sessionStorage,
      );
    const razorpayRecovery =
      getRazorpayRecoveryContext(
        window.sessionStorage,
      );
    const reservationId = String(
      successHold?._id ||
        (recovery?.slug === slug
          ? recovery.reservationId
          : "") ||
        (razorpayRecovery?.slug === slug
          ? razorpayRecovery.reservationId
          : "") ||
        "",
    ).trim();
    const releaseToken = String(
      successHold?.releaseToken ||
        (recovery?.slug === slug
          ? recovery.releaseToken
          : "") ||
        (razorpayRecovery?.slug === slug
          ? razorpayRecovery.releaseToken
          : "") ||
        "",
    ).trim();
    const hasMatchingRecovery = Boolean(
      recovery?.slug === slug &&
        recovery?.reservationId &&
        String(recovery.reservationId) ===
          reservationId,
    );

    const closeAndReset = ({
      preserveRecovery = false,
    } = {}) => {
      onClose?.();
      resetBookingSessionState({
        preserveRecovery,
      });
    };

    if (razorpayResult) {
      closeAndReset();
      return;
    }

    if (razorpayVerificationPayload) {
      closeAndReset({ preserveRecovery: true });
      toast.info(
        t(
          "razorpayPendingCloseText",
          "Your Razorpay payment has already been submitted for server confirmation. Do not pay again; confirmation will continue through the server/webhook and email when completed.",
        ),
        {
          position: "top-center",
          autoClose: 5200,
        },
      );
      return;
    }

    const hasActiveRazorpayOrder = Boolean(
      paymentOrder?.gateway === "RAZORPAY" &&
        paymentOrder?.razorpayOrderId,
    );

    if (hasActiveRazorpayOrder) {
      if (
        holdSecondsRemaining == null ||
        holdSecondsRemaining > 0
      ) {
        const abandonmentState =
          await abandonRazorpayForRetry({
            silent: true,
            refreshSelection: false,
          });

        if (abandonmentState === "completed") {
          return;
        }

        if (abandonmentState === "abandoned_paid") {
          closeAndReset();
          return;
        }

        if (abandonmentState === "abandoned") {
          closeAndReset();

          toast.info(
            t(
              "bookingCancelledReleasedText",
              "Booking cancelled. No payment was confirmed for this attempt, the slot hold was released, and the booking form was reset.",
            ),
            {
              position: "top-center",
              autoClose: 4600,
              toastId:
                "venue-public-razorpay-booking-released",
            },
          );
          return;
        }

        if (abandonmentState === "busy") {
          return;
        }

        // Provider processing/unknown mirrors the eSewa unresolved close path:
        // close the UI but preserve the recovery capability and never allow a
        // second payment until the provider status is known.
        closeAndReset({
          preserveRecovery: true,
        });

        toast.info(
          t(
            "razorpayCloseUnresolvedText",
            "The previous Razorpay payment session could not be released yet. Reopen Booking before starting another payment.",
          ),
          {
            position: "top-center",
            autoClose: 5000,
            toastId:
              "venue-public-razorpay-close-unresolved",
          },
        );
        return;
      }

      closeAndReset();
      return;
    }

    if (!reservationId || !releaseToken) {
      closeAndReset();
      return;
    }

    const hasSubmittedEsewaAttempt = Boolean(
      String(bookingCurrency).toUpperCase() === "NPR" &&
      (paymentStartedRef.current ||
        (hasMatchingRecovery &&
          (Boolean(recovery?.transactionUuid) ||
            [
              "checking",
              "pending",
              "unknown",
            ].includes(esewaRecoveryState)))),
    );

    if (hasSubmittedEsewaAttempt) {
      const abandonmentState =
        await abandonEsewaForRetry({
          silent: true,
        });

      if (abandonmentState === "completed") {
        return;
      }

      if (
        abandonmentState ===
        "abandoned_paid"
      ) {
        closeAndReset();
        return;
      }

      if (abandonmentState === "abandoned") {
        closeAndReset();

        toast.info(
          t(
            "bookingCancelledReleasedText",
            "Booking cancelled. No payment was confirmed for this attempt, the slot hold was released, and the booking form was reset.",
          ),
          {
            position: "top-center",
            autoClose: 4600,
          },
        );
        return;
      }

      if (abandonmentState === "busy") {
        return;
      }

      // Processing/unknown means the server did not confirm a safe release.
      // Preserve only the recovery capability; never manufacture a second pay.
      closeAndReset({
        preserveRecovery: true,
      });

      toast.info(
        t(
          "esewaCloseUnresolvedText",
          "The previous payment session could not be released yet. Reopen Booking before starting another payment.",
        ),
        {
          position: "top-center",
          autoClose: 5000,
        },
      );
      return;
    }

    releaseInFlightRef.current = true;

    try {
      try {
        await releasePublicBookingHold(
          slug,
          reservationId,
          releaseToken,
        );
      } catch (releaseError) {
        if (
          releaseError?.response?.status !==
          404
        ) {
          const hasMatchingRazorpayRecovery = Boolean(
            razorpayRecovery?.slug === slug &&
              razorpayRecovery?.reservationId &&
              String(razorpayRecovery.reservationId) ===
                reservationId,
          );

          closeAndReset({
            preserveRecovery:
              hasMatchingRazorpayRecovery,
          });

          toast.info(
            t(
              hasMatchingRazorpayRecovery
                ? "razorpayCloseUnresolvedText"
                : "bookingReleaseNotConfirmedText",
              hasMatchingRazorpayRecovery
                ? "The Razorpay payment session may already have started. Reopen Booking before starting another payment."
                : "The booking could not be released right now. Please reopen Booking and check availability before trying again.",
            ),
            {
              position: "top-center",
              autoClose: 5000,
            },
          );
          return;
        }
      }

      clearEsewaRecoverySession(
        window.sessionStorage,
        reservationId,
      );
      closeAndReset();

      toast.info(
        t(
          "bookingCancelledReleasedText",
          "Booking cancelled. Your selected slots were released and the booking form was reset.",
        ),
        {
          position: "top-center",
          autoClose: 4200,
        },
      );
    } finally {
      releaseInFlightRef.current = false;
    }
  }, [
    abandonEsewaForRetry,
    abandonRazorpayForRetry,
    bookingCurrency,
    esewaRecoveryState,
    esewaRedirecting,
    holdSecondsRemaining,
    onClose,
    paymentOrder,
    razorpayResult,
    razorpayVerificationPayload,
    paymentLoading,
    resetBookingSessionState,
    slug,
    successHold,
    t,
  ]);

  const paymentRecoveryLoading =
    esewaAbandonmentLoading || razorpayAbandonmentLoading;

  return (
    <ReusableModal
      open={open}
      title={
        selectedGround
          ? `${t("modalTitleWithGroundPrefix", "Book")} ${selectedGroundName}`
          : t("modalTitleWithoutGround", "Book a Slot")
      }
      width="1320px"
      onClose={handleClose}
      closeOnBackdrop={false}
      className={`pv-booking-modal${selectedSlotsOpen ? " is-selected-slots-open" : ""}`}
      style={{
        "--pv-booking-primary": primaryColor,
        "--pv-booking-accent": accentColor,
      }}
    >
      <div
        ref={bookingContentRef}
        className="pv-peer-booking"
        data-lenis-prevent="true"
      >
        <header className="pv-peer-intro pv-peer-intro-compact">
          <h2>{venueName}</h2>
          <p>{selectedGroundName}</p>
        </header>

        <nav
          className="pv-peer-stepper"
          aria-label={t("bookingProgressAriaLabel", "Booking progress")}
        >
          {bookingSteps.map((bookingStep) => {
            const isCurrent = step === bookingStep.number;
            const isComplete = step > bookingStep.number;

            return (
              <div
                className={[
                  "pv-peer-step",
                  isCurrent ? "is-current" : "",
                  isComplete ? "is-complete" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                key={bookingStep.number}
              >
                <span className="pv-peer-step-number">
                  {isComplete ? "✓" : bookingStep.number}
                </span>

                <span className="pv-peer-step-copy">
                  <strong>{bookingStep.title}</strong>
                  <small>{bookingStep.description}</small>
                </span>
              </div>
            );
          })}
        </nav>

        <div
          className={[
            "pv-peer-layout",
            step === 2 ? "is-details-step" : "",
            step === 3 ? "is-payment-step" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div className="pv-peer-main">
            {step === 1 && (
              <section className="pv-peer-card">
                <div className="pv-peer-section-heading is-simple">
                  <div>
                    <h3>{t("stepOneTitle", "Choose your slot")}</h3>
                  </div>
                </div>

                <label className="pv-peer-field">
                  <span>{t("groundLabel", "Ground")}</span>

                  <select
                    value={selectedGroundId}
                    onChange={(event) => {
                      const nextGroundId = event.target.value;

                      if (nextGroundId === selectedGroundId) return;

                      setSelectedGroundId(nextGroundId);
                      setSlots([]);
                      setSelectedItems([]);
                      setSelectedSlotsOpen(false);
                      setPromoCode("");
                      setPromoExpanded(false);
                      setPromoPreview(null);
                      setErrorMessage("");
                    }}
                  >
                    {grounds.map((ground) => (
                      <option key={ground._id} value={ground._id}>
                        {ground.name ||
                          ground.type ||
                          t("groundFallbackName", "Ground")}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="pv-peer-slot-layout">
                  <div className="pv-peer-calendar">
                    <div className="pv-peer-calendar-head">
                      <button
                        type="button"
                        aria-label={t("previousMonthLabel", "Previous month")}
                        disabled={!canViewPreviousMonth}
                        onClick={() =>
                          setCalendarMonth(
                            new Date(
                              calendarMonth.getFullYear(),
                              calendarMonth.getMonth() - 1,
                              1,
                            ),
                          )
                        }
                      >
                        <ChevronLeft size={18} strokeWidth={2.2} />
                      </button>

                      <strong>{calendarMonthLabel}</strong>

                      <button
                        type="button"
                        aria-label={t("nextMonthLabel", "Next month")}
                        disabled={!canViewNextMonth}
                        onClick={() =>
                          setCalendarMonth(
                            new Date(
                              calendarMonth.getFullYear(),
                              calendarMonth.getMonth() + 1,
                              1,
                            ),
                          )
                        }
                      >
                        <ChevronRight size={18} strokeWidth={2.2} />
                      </button>
                    </div>

                    <div className="pv-peer-calendar-weekdays">
                      {calendarWeekdays.map((weekday) => (
                        <span key={weekday}>{weekday}</span>
                      ))}
                    </div>

                    <div className="pv-peer-calendar-grid">
                      {calendarDays.map((day) => {
                        const isSelected =
                          isPublicBookingCalendarDateSelected({
                            dayYmd: day.ymd,
                            activeDate: selectedDate,
                            selectedItems,
                          });

                        const isToday = day.ymd === currentTodayYmd;

                        const isOutsideBookingWindow =
                          day.ymd < currentTodayYmd ||
                          day.ymd > maxBookingYmd;

                        return (
                          <button
                            type="button"
                            key={day.ymd}
                            disabled={isOutsideBookingWindow}
                            aria-pressed={isSelected}
                            className={[
                              "pv-peer-calendar-day",
                              !day.isCurrentMonth ? "is-outside" : "",
                              isSelected ? "is-selected" : "",
                              isToday ? "is-today" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            onClick={() => {
                              setSelectedDate(day.ymd);
                              setErrorMessage("");

                              if (!day.isCurrentMonth) {
                                setCalendarMonth(
                                  new Date(
                                    day.date.getFullYear(),
                                    day.date.getMonth(),
                                    1,
                                  ),
                                );
                              }
                            }}
                          >
                            {day.dayNumber}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div
                    className={[
                      "pv-peer-slots",
                      allSlotsBooked ? "is-all-booked" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <div className="pv-peer-slots-head">
                      <div>
                        <span>{t("availableTimesLabel", "Available times")}</span>
                        <strong>{selectedDateLabel}</strong>
                      </div>

                      <small>{selectedGroundName}</small>
                    </div>

                    <div
                      className="pv-peer-slot-legend"
                      aria-label={t("slotLegendLabel", "Slot status")}
                    >
                      <span className="pv-peer-slot-legend-item">
                        <i className="pv-peer-slot-legend-dot is-booked" aria-hidden="true" />
                        {t("slotBookedLegend", "Booked")}
                      </span>
                      <span className="pv-peer-slot-legend-item">
                        <i className="pv-peer-slot-legend-dot is-available" aria-hidden="true" />
                        {t("slotAvailableLegend", "Available")}
                      </span>
                      <span className="pv-peer-slot-legend-item">
                        <i className="pv-peer-slot-legend-dot is-selected" aria-hidden="true" />
                        {t("slotSelectedLegend", "Selected")}
                      </span>
                    </div>

                    {loadingSlots ? (
                      <div className="pv-booking-empty pv-booking-empty--loading">
                        <LoaderCircle
                          className="pv-booking-slot-loader"
                          size={19}
                          strokeWidth={2.2}
                          aria-hidden="true"
                        />
                        <span>
                          {t(
                            "loadingSlotsText",
                            "Loading available slots...",
                          )}
                        </span>
                      </div>
                    ) : allSlotsBooked ? (
                      <div
                        className="pv-booking-empty pv-booking-empty--all-booked"
                        role="status"
                        aria-live="polite"
                      >
                        <CalendarX
                          className="pv-booking-empty-icon"
                          size={34}
                          strokeWidth={1.8}
                          aria-hidden="true"
                        />
                        <strong>
                          {t(
                            "allSlotsBookedTitle",
                            "All slots are booked for today!",
                          )}
                        </strong>
                        <span>
                          {t(
                            "allSlotsBookedDescription",
                            "Try another date or venue!",
                          )}
                        </span>
                      </div>
                    ) : slots.length ? (
                      <div className="pv-peer-slot-grid">
                        {slots.map((slot) => {
                          const start = getSlotStart(slot);
                          const end = getSlotEnd(slot);
                          const key = `${start}-${end}`;
                          const available = isSlotAvailable(slot);

                          const selected = selectedItems.some(
                            (item) =>
                              item.date === selectedDate &&
                              item.start === start &&
                              item.end === end,
                          );

                          return (
                            <button
                              type="button"
                              key={key}
                              disabled={!available}
                              className={[
                                "pv-peer-slot",
                                !available ? "is-booked" : "is-available",
                                selected ? "is-selected" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              onClick={() => toggleSlotSelection(slot)}
                              aria-pressed={selected}
                              data-selected={selected ? "true" : "false"}
                              aria-label={`${getSlotLabel(
                                slot,
                                t("slotFallbackLabel", "Slot"),
                              )}. ${available
                                ? getSlotPriceLabel(
                                    slot,
                                    t(
                                      "priceOnRequestLabel",
                                      "Price on request",
                                    ),
                                  )
                                : t(
                                    "slotBookedLabel",
                                    "Booked",
                                  )}${selected
                                ? `. ${t(
                                    "slotSelectedLegend",
                                    "Selected",
                                  )}`
                                : ""}`}
                            >
                              <strong className="pv-peer-slot-time">
                                <span className="pv-peer-slot-time-full">
                                  {getSlotLabel(
                                    slot,
                                    t("slotFallbackLabel", "Slot"),
                                  )}
                                </span>
                                <span className="pv-peer-slot-time-compact">
                                  {getCompactSlotLabel(
                                    slot,
                                    t("slotFallbackLabel", "Slot"),
                                  )}
                                </span>
                              </strong>

                              <span className="pv-peer-slot-price">
                                {available
                                  ? getSlotPriceLabel(
                                    slot,
                                    t(
                                      "priceOnRequestLabel",
                                      "Price on request",
                                    ),
                                  )
                                  : t(
                                    "slotBookedLabel",
                                    "Booked",
                                  )}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="pv-booking-empty">
                        {t(
                          "noSlotsText",
                          "No slots are available for this date.",
                        )}
                      </div>
                    )}

                  </div>
                </div>

                <button
                  type="button"
                  className="pv-peer-selected-count pv-peer-selected-trigger"
                  onClick={() => setSelectedSlotsOpen(true)}
                  disabled={!selectedItems.length}
                  aria-haspopup="dialog"
                  aria-expanded={selectedSlotsOpen}
                >
                  <span className="pv-peer-selected-trigger-copy">
                    <span className="pv-peer-selected-trigger-icon" aria-hidden="true">
                      <CalendarDays size={16} strokeWidth={2.1} />
                    </span>
                    <span className="pv-peer-selected-trigger-text">
                      <strong>
                        {selectedItems.length}{" "}
                        {selectedItems.length === 1
                          ? t("slotSelectedCountSingle", "Slot Selected")
                          : t("slotSelectedCountMultiple", "Slots Selected")}
                      </strong>
                      <small>
                        {bookingCurrency} {bookingSummary.subtotal}
                      </small>
                    </span>
                  </span>
                  <span className="pv-peer-selected-trigger-view">
                    <span>{t("viewSelectedSlotsLabel", "View selected")}</span>
                    <span className="pv-peer-selected-trigger-arrow" aria-hidden="true">
                      <ArrowRight size={17} strokeWidth={2.2} />
                    </span>
                  </span>
                </button>

                <div className="pv-peer-actions">
                  <button
                    type="button"
                    className="pv-peer-secondary-button"
                    onClick={handleClose}
                  >
                    {t("cancelLabel", "Cancel")}
                  </button>

                  <button
                    type="button"
                    className="pv-peer-primary-button"
                    onClick={handleContinueToDetails}
                    disabled={!selectedItems.length || loadingSlots}
                  >
                    {t(
                      "continueDetailsLabel",
                      "Continue to Details",
                    )}
                  </button>
                </div>
              </section>
            )}

            {step === 2 && (
              <form
                id="pv-public-booking-details-form"
                className="pv-peer-card"
                onSubmit={handleSubmit}
              >
                <div className="pv-peer-section-heading is-simple">
                  <div>
                    <h3>{t("detailsTitle", "Your details")}</h3>
                  </div>
                </div>

                <div className="pv-peer-details-grid">
                  <label className="pv-peer-field">
                    <span>{t("fullNameLabel", "Full name")}</span>

                    <input
                      type="text"
                      value={customer.userName}
                      onChange={(event) =>
                        setCustomerField("userName", event.target.value)
                      }
                      placeholder={t(
                        "fullNamePlaceholder",
                        "Enter your full name",
                      )}
                      autoComplete="name"
                    />
                  </label>

                  <label className="pv-peer-field">
                    <span>
                      {t("phoneLabel", "Phone")} {
                        !requirePhone && (
                          <small>{t("optionalLabel", "(optional)")}</small>
                        )
                      }
                    </span>

                    <PublicContactPhoneField
                      value={customer.contactNumber}
                      countryCode={phoneCountryCode}
                      onChange={(event) => {
                        const fieldName = event?.target?.name;
                        const nextValue = event?.target?.value ?? "";

                        setPromoPreview(null);

                        if (fieldName === "phoneCountryCode") {
                          setPhoneCountryCode(nextValue);
                          return;
                        }

                        if (fieldName === "phone") {
                          setCustomerField("contactNumber", nextValue);
                        }
                      }}
                      placeholder={t("phonePlaceholder", "9876543210")}
                      required={requirePhone}
                    />
                  </label>

                  <label className="pv-peer-field pv-peer-field-full">
                    <span>
                      {t("emailLabel", "Email")} {
                        !requireEmail && (
                          <small>{t("optionalLabel", "(optional)")}</small>
                        )
                      }
                    </span>

                    <input
                      type="email"
                      value={customer.email}
                      onChange={(event) =>
                        setCustomerField("email", event.target.value)
                      }
                      placeholder={t(
                        "emailPlaceholder",
                        "customer@example.com",
                      )}
                      autoComplete="email"
                    />

                    <small className="pv-peer-email-note">
                      {t(
                        "emailConfirmationNote",
                        "You will receive your booking confirmation here after successful payment.",
                      )}
                    </small>
                  </label>
                </div>

                <div className="pv-peer-details-mobile-summary">
                  <div className="pv-peer-details-mobile-summary-head">
                    <div>
                      <span>{t("selectedSlotsTitle", "Selected slots")}</span>
                      <strong>
                        {selectedItems.length} {selectedItems.length === 1
                          ? t("selectedSlotShortSingle", "slot")
                          : t("selectedSlotShortMultiple", "slots")}
                      </strong>
                    </div>

                    <button
                      type="button"
                      onClick={handleBackToSlots}
                    >
                      <PencilLine size={14} strokeWidth={2.1} />
                      {t("editDetailsLabel", "Edit")}
                    </button>
                  </div>

                  <div className="pv-peer-details-mobile-summary-groups">
                    {selectedItemGroups.map((group) => (
                      <div className="pv-peer-details-mobile-summary-group" key={group.date}>
                        <div className="pv-peer-details-mobile-summary-date">
                          <CalendarDays size={16} strokeWidth={2.1} aria-hidden="true" />
                          <strong>{formatBookingDate(group.date)}</strong>
                        </div>

                        {group.items.map((item) => (
                          <div
                            className="pv-peer-details-mobile-summary-row"
                            key={getSelectedItemKey(item)}
                          >
                            <span>
                              {formatSlotTime(item.start)} – {formatSlotTime(item.end)}
                            </span>
                            <strong>
                              {getPublicBookingSlotPriceLabel(item, {
                                fallbackCurrency: bookingCurrency,
                                fallback: `${bookingCurrency} 0`,
                              })}
                            </strong>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pv-peer-actions">
                  <button
                    type="button"
                    className="pv-peer-secondary-button"
                    onClick={handleBackToSlots}
                  >
                    {t("backLabel", "Back")}
                  </button>

                  <button
                    type="submit"
                    className="pv-peer-primary-button"
                  >
                    {t("continuePayLabel", "Continue to Pay")}
                  </button>
                </div>
              </form>
            )}

            {step === 3 && (
              <section
                className={[
                  "pv-peer-card",
                  "pv-peer-payment-card",
                  paymentRecoveryLoading ? "is-esewa-abandoning" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-busy={paymentRecoveryLoading}
              >
                <div
                  className={[
                    "pv-esewa-return-loader",
                    paymentRecoveryLoading ? "is-visible" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  role="status"
                  aria-live="polite"
                  aria-hidden={!paymentRecoveryLoading}
                >
                  <div className="pv-esewa-return-loader__panel">
                    <span
                      className="pv-esewa-return-loader__icon"
                      aria-hidden="true"
                    >
                      <LoaderCircle size={30} strokeWidth={2.3} />
                    </span>
                    <div className="pv-esewa-return-loader__copy">
                      <strong>
                        {razorpayAbandonmentLoading
                          ? t(
                              "razorpayRecoveryLoadingTitle",
                              "Finishing your previous Razorpay attempt…",
                            )
                          : t(
                              "esewaBrowserBackLoadingTitle",
                              "Finishing your previous eSewa attempt…",
                            )}
                      </strong>
                      <span>
                        {razorpayAbandonmentLoading
                          ? t(
                              "razorpayRecoveryLoadingText",
                              "Please wait while we check the payment and release the booking safely.",
                            )
                          : t(
                              "esewaBrowserBackLoadingText",
                              "Please wait while we check the previous booking.",
                            )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pv-peer-section-heading pv-peer-payment-heading pv-peer-review-pay-heading-legacy">
                  <div>
                    <span>{t("reviewPayEyebrow", "Final step")}</span>
                    <h3>{t("reviewPayTitle", "Review & Pay")}</h3>
                    <p>
                      {t(
                        "reviewPayDescription",
                        "Review your booking details and complete the payment.",
                      )}
                    </p>
                  </div>
                </div>

                <div className="pv-peer-review-details">
                  <div className="pv-peer-review-details-head">
                    <div>
                      <span>
                        {t("contactDetailsTitle", "Contact details")}
                      </span>
                      <small>
                        {t(
                          "contactDetailsReviewHint",
                          "Confirm who this booking is for before paying.",
                        )}
                      </small>
                    </div>

                    {!successHold &&
                      esewaRecoveryState === "idle" && (
                        <button
                          type="button"
                          className="pv-peer-edit-details"
                          onClick={handleBackToDetails}
                        >
                          <PencilLine size={15} strokeWidth={2.1} />
                          {t("editDetailsLabel", "Edit")}
                        </button>
                      )}
                  </div>

                  <div className="pv-peer-review-details-grid">
                    <div>
                      <span>{t("fullNameLabel", "Full name")}</span>
                      <strong>{customer.userName.trim()}</strong>
                    </div>
                    <div>
                      <span>{t("phoneLabel", "Phone")}</span>
                      <strong>{customerPhoneDisplay}</strong>
                    </div>
                    <div className="is-full">
                      <span>{t("emailLabel", "Email")}</span>
                      <strong>{customerEmailDisplay}</strong>
                    </div>
                  </div>
                </div>

                <div className="pv-peer-payment-method-label">
                  <strong>{t("paymentMethodTitle", "Payment method")}</strong>
                </div>

                <div
                  className={[
                    "pv-peer-payment-choice",
                    String(bookingCurrency).toUpperCase() === "NPR"
                      ? "is-esewa"
                      : "is-razorpay",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div
                    className={[
                      "pv-peer-payment-icon",
                      String(bookingCurrency).toUpperCase() === "NPR"
                        ? "is-esewa"
                        : "is-razorpay",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {String(bookingCurrency).toUpperCase() === "NPR" ? (
                      <img
                        src={esewaLogo}
                        alt={t("esewaLogoAlt", "eSewa")}
                      />
                    ) : (
                      <img
                        src={razorpayLogo}
                        alt={t("razorpayLogoAlt", "Razorpay")}
                      />
                    )}
                  </div>

                  <div>
                    <strong>
                      {String(bookingCurrency).toUpperCase() === "NPR"
                        ? t(
                          "secureEsewaTitle",
                          "Pay securely with eSewa",
                        )
                        : t(
                          "securePaymentTitle",
                          "Secure online payment",
                        )}
                    </strong>

                    <span>
                      {String(bookingCurrency).toUpperCase() === "NPR"
                        ? t(
                          "esewaGatewayDescription",
                          "You’ll be redirected to eSewa to complete your payment securely.",
                        )
                        : t(
                          "gatewayDescription",
                          "Razorpay Checkout will open securely so you can complete your payment.",
                        )}
                    </span>
                  </div>

                  <span className="pv-peer-payment-selected" aria-hidden="true">
                    <Check size={16} strokeWidth={2.6} />
                  </span>
                </div>

                {[
                  "checking",
                  "pending",
                  "unknown",
                ].includes(esewaRecoveryState) && (
                  <div
                    className="pv-peer-payment-recovery"
                    aria-live="polite"
                  >
                    <div>
                      <strong>
                        {esewaRecoveryState === "checking"
                          ? t(
                              "checkingEsewaStatusTitle",
                              "Checking your eSewa payment",
                            )
                          : esewaRecoveryState === "pending"
                            ? t(
                                "pendingEsewaStatusTitle",
                                "Payment is still pending",
                              )
                            : t(
                                "unknownEsewaStatusTitle",
                                "Payment status needs another check",
                              )}
                      </strong>
                      <span>
                        {holdSecondsRemaining === 0
                          ? t(
                              "esewaExpiredRecoveryHelpText",
                              "The 5-minute payment window has ended. We are checking eSewa before releasing the slot.",
                            )
                          : t(
                              "esewaRecoveryHelpText",
                              "Do not start another payment while this transaction is being checked.",
                            )}
                      </span>
                    </div>

                    {esewaRecoveryState !== "checking" && (
                      <button
                        type="button"
                        onClick={() =>
                          reconcileEsewaPayment({
                            silent: false,
                          })
                        }
                      >
                        {t("checkPaymentStatusLabel", "Check status")}
                      </button>
                    )}
                  </div>
                )}

                {holdSecondsRemaining != null &&
                  !["checking", "pending", "unknown"].includes(
                    esewaRecoveryState,
                  ) && (
                    <p
                    className={[
                      "pv-peer-payment-timer",
                      holdSecondsRemaining === 0 ? "is-expired" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-live="polite"
                  >
                    {holdSecondsRemaining > 0 ? (
                      <>
                        {t(
                          "paymentCountdownPrefix",
                          "Please complete payment within",
                        )}{" "}
                        <strong>
                          {formatHoldCountdown(holdSecondsRemaining)}
                        </strong>
                      </>
                    ) : (
                      <strong>
                        {String(bookingCurrency).toUpperCase() === "NPR"
                          ? t(
                              "paymentWindowEndedText",
                              "Payment window ended — checking eSewa status…",
                            )
                          : t(
                              "razorpayPaymentWindowEndedText",
                              "Payment window ended. If money was debited, do not pay again; wait for confirmation or contact support.",
                            )}
                      </strong>
                    )}
                  </p>
                )}

                {paymentOrder &&
                  String(bookingCurrency).toUpperCase() !== "NPR" && (
                  <div className="pv-payment-result">
                    <span>
                      {razorpayResult
                        ? t(
                            "razorpayConfirmedTitle",
                            "Payment confirmed",
                          )
                        : razorpayPaymentPending
                          ? t(
                              "razorpayPendingTitle",
                              "Payment confirmation pending",
                            )
                          : t(
                              "paymentOrderCreatedText",
                              "Razorpay payment ready",
                            )}
                    </span>

                    <div>
                      <strong>{t("gatewayLabel", "Gateway:")}</strong>{" "}
                      {paymentOrder.gateway || "RAZORPAY"}
                    </div>

                    <div>
                      <strong>{t("amountLabel", "Amount:")}</strong>{" "}
                      {paymentOrder.currency || bookingCurrency}{" "}
                      {paymentOrder.amount
                        ? Number(paymentOrder.amount) / 100
                        : "—"}
                    </div>

                    {razorpayPaymentPending &&
                      !razorpayResult && (
                        <div>
                          {t(
                            "razorpayPendingHelpText",
                            "Payment details were received from Razorpay. Do not pay again; use Check payment status while the server or webhook completes confirmation.",
                          )}
                        </div>
                      )}

                    {razorpayResult ? (
                      <>
                        <div>
                          <strong>
                            {t(
                              "bookingReferenceLabel",
                              "Booking reference:",
                            )}
                          </strong>{" "}
                          {razorpayResult.generatedReservationId ||
                            razorpayResult.reservationId ||
                            "—"}
                        </div>
                        <div>
                          {t(
                            "razorpayConfirmedHelpText",
                            "Your payment was verified by the server and the booking is confirmed.",
                          )}
                        </div>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="pv-payment-link"
                        onClick={handleRetryRazorpayPayment}
                        disabled={
                          paymentLoading ||
                          razorpayVerifying ||
                          (razorpayPaymentPending &&
                            !razorpayVerificationPayload) ||
                          (!razorpayVerificationPayload &&
                            holdSecondsRemaining === 0)
                        }
                      >
                        {razorpayVerifying
                          ? t(
                              "razorpayVerifyingLabel",
                              "Verifying payment...",
                            )
                          : razorpayVerificationPayload
                            ? t(
                                "razorpayCheckStatusLabel",
                                "Check payment status",
                              )
                            : razorpayPaymentPending
                              ? t(
                                  "razorpayProcessingLabel",
                                  "Payment processing...",
                                )
                            : paymentLoading
                              ? t(
                                  "razorpayOpeningLabel",
                                  "Opening Razorpay...",
                                )
                              : t(
                                  "payRazorpayLabel",
                                  "Pay with Razorpay",
                                )}
                      </button>
                    )}
                  </div>
                )}

                <div className="pv-peer-actions">
                  {!successHold &&
                    esewaRecoveryState === "idle" &&
                    !paymentOrder && (
                      <button
                        type="button"
                        className="pv-peer-secondary-button"
                        onClick={handleBackToDetails}
                      >
                        <ArrowLeft size={15} strokeWidth={2.1} />
                        {t("backToDetailsLabel", "Back to Details")}
                      </button>
                    )}

                  {razorpayResult ? (
                    <button
                      type="button"
                      className="pv-peer-primary-button"
                      onClick={() => {
                        onClose?.();
                        resetBookingSessionState();
                      }}
                    >
                      {t("doneLabel", "Done")}
                    </button>
                  ) : paymentOrder &&
                    String(bookingCurrency).toUpperCase() !== "NPR" ? null : (
                    <button
                      type="button"
                      className="pv-peer-primary-button"
                      onClick={handleCreatePaymentOrder}
                      disabled={
                        !selectedItems.length ||
                        paymentLoading ||
                        razorpayVerifying ||
                        esewaRedirecting ||
                        promoApplying ||
                        !!paymentOrder ||
                        holdSecondsRemaining === 0 ||
                        [
                          "checking",
                          "pending",
                          "unknown",
                        ].includes(esewaRecoveryState)
                      }
                    >
                      {paymentLoading
                        ? t(
                            "creatingPaymentLabel",
                            "Creating Payment...",
                          )
                        : paymentOrder
                          ? t(
                              "paymentOrderCreatedButtonLabel",
                              "Payment Order Created",
                            )
                          : String(bookingCurrency).toUpperCase() === "NPR"
                            ? t("payEsewaLabel", "Pay via eSewa")
                            : t(
                                "payRazorpayLabel",
                                "Pay with Razorpay",
                              )}
                    </button>
                  )}
                </div>
              </section>
            )}
          </div>

          <aside
            className={[
              "pv-peer-summary-card",
              step === 2 ? "is-details-step" : "",
              step === 3 ? "is-payment-step" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div className="pv-peer-summary-venue">
              {bookingSummaryImage ? (
                <img
                  src={bookingSummaryImage}
                  alt={`${venueName} - ${selectedGroundName}`}
                />
              ) : null}

              <div>
                <strong>{venueName}</strong>
                <small>{selectedGroundName}</small>
              </div>
            </div>

            <div className="pv-peer-summary-slots">
              <div className="pv-peer-summary-label">
                <span>
                  {selectedItems.length === 1
                    ? t("oneSlotSelectedLabel", "1 Slot Selected")
                    : `${selectedItems.length} ${t(
                        "slotsSelectedLabel",
                        "Slots Selected",
                      )}`}
                </span>
              </div>

              {selectedItems.length ? (
                <div className="pv-peer-summary-list">
                  {selectedItemGroups.map((group) => (
                    <div
                      className="pv-peer-summary-date-group"
                      key={group.date}
                    >
                      <div className="pv-peer-summary-date-head">
                        <span
                          className="pv-peer-summary-calendar-icon"
                          aria-hidden="true"
                        >
                          <CalendarDays size={18} strokeWidth={2} />
                        </span>

                        <strong>{formatBookingDate(group.date)}</strong>
                        <small>
                          {group.items.length}{" "}
                          {group.items.length === 1
                            ? t("selectedSlotShortSingle", "slot")
                            : t("selectedSlotShortMultiple", "slots")}
                        </small>
                      </div>

                      <div className="pv-peer-summary-date-slots">
                        {group.items.map((item) => (
                          <div
                            className="pv-peer-summary-time-row"
                            key={getSelectedItemKey(item)}
                          >
                            <span>
                              {formatSlotTime(item.start)} –{" "}
                              {formatSlotTime(item.end)}
                            </span>

                            <div className="pv-peer-summary-row-actions">
                              <strong className="pv-peer-summary-price">
                                {getPublicBookingSlotPriceLabel(item, {
                                  fallbackCurrency: bookingCurrency,
                                  fallback: `${bookingCurrency} 0`,
                                })}
                              </strong>

                              {!successHold && (
                                <button
                                  type="button"
                                  onClick={() => removeSelectedItem(item)}
                                  aria-label={`Remove ${formatSlotTime(
                                    item.start,
                                  )} to ${formatSlotTime(item.end)}`}
                                >
                                  <X size={15} strokeWidth={2.2} />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="pv-peer-summary-empty">
                  {t(
                    "selectedSlotsEmptyText",
                    "Your selected slots will appear here.",
                  )}
                </p>
              )}
            </div>

            <div className="pv-peer-payment-summary">
              <h4>{t("paymentSummaryTitle", "Payment Summary")}</h4>

              <div className="pv-peer-payment-summary-row">
                <span>{t("subTotalLabel", "Sub Total")}</span>
                <strong>
                  {bookingCurrency} {bookingSummary.subtotal}
                </strong>
              </div>

              {bookingSummary.discountAmount > 0 && (
                <div className="pv-peer-payment-summary-row is-discount">
                  <span>{t("discountLabel", "Discount")}</span>
                  <strong>
                    - {bookingCurrency} {bookingSummary.discountAmount}
                  </strong>
                </div>
              )}

              <div className="pv-peer-payment-summary-row is-total">
                <span>{t("totalPayableLabel", "Total Payable")}</span>
                <strong>
                  {bookingCurrency} {bookingSummary.finalAmount}
                </strong>
              </div>
            </div>




            {step === 3 && !successHold && (
              <div
                className={[
                  "pv-peer-promo-card",
                  promoPreview ? "is-applied" : "",
                  promoExpanded ? "is-open" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {promoPreview ? (
                  <div
                    className="pv-peer-promo-applied"
                    aria-live="polite"
                  >
                    <span
                      className="pv-peer-promo-applied-icon"
                      aria-hidden="true"
                    >
                      <CheckCircle2 size={20} strokeWidth={2.3} />
                    </span>

                    <div>
                      <strong>{promoPreview.code}</strong>
                      <span>
                        {promoPreview.description ||
                          t(
                            "promoAppliedSuffix",
                            "Promo applied",
                          )}
                        {promoPreview.discountAmount > 0
                          ? ` · ${t("youSavedLabel", "You saved")} ${bookingCurrency} ${promoPreview.discountAmount}`
                          : ""}
                      </span>
                    </div>

                    <button
                      type="button"
                      className="pv-peer-promo-remove"
                      onClick={handleRemovePromo}
                      aria-label={t(
                        "removePromoLabel",
                        "Remove promo code",
                      )}
                    >
                      <X size={18} strokeWidth={2.2} />
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="pv-peer-promo-trigger"
                      aria-expanded={promoExpanded}
                      onClick={() =>
                        setPromoExpanded((value) => !value)
                      }
                    >
                      <span
                        className="pv-peer-promo-icon"
                        aria-hidden="true"
                      >
                        <Tag size={20} strokeWidth={2.1} />
                      </span>
                      <strong>
                        {t(
                          "havePromoCodeLabel",
                          "Have a Promo Code?",
                        )}
                      </strong>
                      <span
                        className="pv-peer-promo-chevron"
                        aria-hidden="true"
                      >
                        <ChevronDown size={20} strokeWidth={2.2} />
                      </span>
                    </button>

                    {promoExpanded && (
                      <div className="pv-peer-promo-entry">
                        <div className="pv-peer-promo-input-row">
                          <input
                            type="text"
                            value={promoCode}
                            onChange={(event) =>
                              setPromoCode(
                                event.target.value.toUpperCase(),
                              )
                            }
                            placeholder={t(
                              "promoCodePlaceholder",
                              "Enter promo code",
                            )}
                            autoComplete="off"
                            aria-label={t(
                              "promoCodeLabel",
                              "Promo code",
                            )}
                            disabled={
                              promoApplying ||
                              paymentLoading
                            }
                          />

                          <button
                            type="button"
                            className="pv-peer-promo-apply"
                            onClick={handleApplyPromo}
                            disabled={
                              promoApplying ||
                              paymentLoading ||
                              !promoCode.trim()
                            }
                          >
                            {promoApplying
                              ? t(
                                  "promoApplyingLabel",
                                  "Applying...",
                                )
                              : t(
                                  "promoApplyButtonLabel",
                                  "Apply",
                                )}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}


          </aside>
        </div>

        {selectedSlotsOpen && (
          <div
            className="pv-selected-slots-overlay"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setSelectedSlotsOpen(false);
              }
            }}
          >
            <section
              className="pv-selected-slots-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pv-selected-slots-title"
            >
              <header className="pv-selected-slots-head">
                <div>
                  <span>{selectedGroundName}</span>
                  <h3 id="pv-selected-slots-title">
                    {t("selectedSlotsTitle", "Selected slots")}
                  </h3>
                  <p>
                    {selectedItems.length}{" "}
                    {selectedItems.length === 1
                      ? t("slotSelectedCountSingle", "slot selected")
                      : t("slotSelectedCountMultiple", "slots selected")}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedSlotsOpen(false)}
                  aria-label={t("closeSelectedSlotsLabel", "Close selected slots")}
                >
                  <X size={20} strokeWidth={2.2} />
                </button>
              </header>

              <div className="pv-selected-slots-body">
                {selectedItemGroups.map((group) => (
                  <div className="pv-selected-slots-group" key={group.date}>
                    <div className="pv-selected-slots-date">
                      <CalendarDays size={18} strokeWidth={2.1} />
                      <strong>{formatBookingDate(group.date)}</strong>
                      <span>{group.items.length}</span>
                    </div>

                    <div className="pv-selected-slots-list">
                      {group.items.map((item) => (
                        <div
                          className="pv-selected-slots-row"
                          key={getSelectedItemKey(item)}
                        >
                          <div>
                            <strong>
                              {formatSlotTime(item.start)} –{" "}
                              {formatSlotTime(item.end)}
                            </strong>
                          </div>

                          <div className="pv-selected-slots-row-actions">
                            <strong>
                              {getPublicBookingSlotPriceLabel(item, {
                                fallbackCurrency: bookingCurrency,
                                fallback: `${bookingCurrency} 0`,
                              })}
                            </strong>

                            {!successHold && (
                              <button
                                type="button"
                                onClick={() => removeSelectedItem(item)}
                                aria-label={`Remove ${formatSlotTime(
                                  item.start,
                                )} to ${formatSlotTime(item.end)}`}
                              >
                                <X size={15} strokeWidth={2.2} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <footer className="pv-selected-slots-footer">
                <div>
                  <span>{t("selectedSlotsTotalLabel", "Selected total")}</span>
                  <strong>
                    {bookingCurrency} {bookingSummary.subtotal}
                  </strong>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedSlotsOpen(false)}
                >
                  {t("doneLabel", "Done")}
                </button>
              </footer>
            </section>
          </div>
        )}
      </div>
    </ReusableModal>
  );
};

export default PublicBookingModal;
